import React, { useState, useEffect } from 'react';
import { 
  FileText, 
  Edit,  
  Sparkles, 
  ArrowRight, 
  Target,
  ChevronRight,
  Play,
  Award,
  MessageSquare,
  CheckCircle,
  Briefcase,
  User,
  Search,
  Building2,
  BookOpen,
  Rocket,
  Heart
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import LandingNavbar from '../components/LandingNavbar';
import PricingSection from '../components/PricingSection';
import { getContent } from '../data/landingContent';
import findJobSectionImg from '../assets/findJobSection.png';
import companiesImg from '../assets/cvlogs companies.jpg';
import coverLetterImg from '../assets/cover letter.png';
import cvSectionHeroImg from '../assets/CV-section.png';
import footerLogo from '../assets/logo.png';
import LegalDocumentsModal from '../components/Legal/LegalDocumentsModal';
import TeamSection from '../components/TeamSection';

export default function Home() {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const isRTL = language === 'ar';
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [aboutAnimationStep, setAboutAnimationStep] = useState(0);
  const [isLegalModalOpen, setIsLegalModalOpen] = useState(false);
  const [requestedLegalSlug, setRequestedLegalSlug] = useState(null);
  const [activeMockTab, setActiveMockTab] = useState('cv');
  const [activeLearningSlide, setActiveLearningSlide] = useState(0);
  const [jobSearchQuery, setJobSearchQuery] = useState('');
  const [activeJobSource, setActiveJobSource] = useState('linkedin');
  const [coverLetterText, setCoverLetterText] = useState('');
  const [coverLetterStep, setCoverLetterStep] = useState(0);
  const [recruiterStep, setRecruiterStep] = useState(0);
  const [recruiterMatchScore, setRecruiterMatchScore] = useState(45);
  const [cvBuilderStep, setCvBuilderStep] = useState(0);
  const [cvSummaryText, setCvSummaryText] = useState('');
  const [cvSkillsList, setCvSkillsList] = useState([]);
  const [beforeAfterVal, setBeforeAfterVal] = useState(50);

  // Agent Chat Simulator states
  const [simStep, setSimStep] = useState(0);
  const [simMessages, setSimMessages] = useState([]);
  const [simUserText, setSimUserText] = useState('');
  const [cursorX, setCursorX] = useState('15%');
  const [cursorY, setCursorY] = useState('85%');
  const [cursorOpacity, setCursorOpacity] = useState(0);
  const [isCtaHovered, setIsCtaHovered] = useState(false);
  const [clickPulse, setClickPulse] = useState(false);

  // Agent Chat Simulator Loop
  useEffect(() => {
    let timeoutId;
    let typeInterval;
    
    const messagesEN = {
      greeting: language === 'tr' ? 'Merhaba! Ben Yapay Zeka Kariyer Koçunuzum. Size nasıl yardımcı olabilirim?' : language === 'ar' ? 'مرحباً! أنا مدربك المهني بالذكاء الاصطناعي. كيف يمكنني مساعدتك اليوم؟' : 'Hello! I am your AI Career Coach. How can I help you today?',
      userQuestion: language === 'tr' ? 'CV\'mi inceleyip ATS filtreleri için optimize edebilir misin?' : language === 'ar' ? 'هل يمكنك مراجعة سيرتي الذاتية وتحسينها لتخطي فلاتر ATS؟' : 'Can you review my CV and optimize it for ATS filters?',
      aiResponse: "{language === 'tr' ? 'Kesinlikle! Özgeçmişinizi analiz edebilir, anahtar kelimeleri optimize edebilir ve puanınızı %95\'e çıkarabilirim. Kontrol panelini açmak ve denemek için aşağıdaki düğmeye tıklayın!' : language === 'ar' ? 'بالتأكيد! يمكنني تحليل سيرتك الذاتية، وتحسين الكلمات المفتاحية، ورفع تقييمك إلى 95%. اضغط على الزر أدناه لفتح لوحة التحكم وتجربتها!' : 'Absolutely! I can analyze your resume, optimize keywords, and boost your score to 95%. Click the button below to open the dashboard and try it out!'} ✨",
      cta: "Try AI Assistant Now 🚀"
    };

    const messagesAR = {
      greeting: "مرحباً! أنا مساعدك المهني الذكي. كيف يمكنني مساعدتك اليوم؟",
      userQuestion: "ممكن تراجع السيرة الذاتية بتاعتي وتخليها متوافقة مع أنظمة الفرز (ATS)؟",
      aiResponse: "بالتأكيد! سأقوم بفحص سيرتك الذاتية، وتحسين الكلمات المفتاحية، ورفع نسبة التوافق لـ 95%. اضغط على الزر أدناه لبدء السحر! ✨",
      cta: "جرب المساعد الذكي الآن 🚀"
    };

    const script = language === 'ar' ? messagesAR : messagesEN;

    const runSimulation = () => {
      // Step 0: Set Initial AI Greeting
      setSimStep(0);
      setSimMessages([{ sender: 'ai', text: script.greeting }]);
      setSimUserText('');
      setCursorOpacity(0);
      // Reset cursor position based on layout alignment
      setCursorX(isRTL ? '80%' : '20%');
      setCursorY('75%');
      setIsCtaHovered(false);
      setClickPulse(false);

      // Step 1: Start User typing after 2.5 seconds
      timeoutId = setTimeout(() => {
        setSimStep(1);
        let currentText = '';
        const fullText = script.userQuestion;
        let charIndex = 0;
        
        typeInterval = setInterval(() => {
          if (charIndex < fullText.length) {
            currentText += fullText[charIndex];
            setSimUserText(currentText);
            charIndex++;
          } else {
            clearInterval(typeInterval);
            
            // Step 2: User Message Sent
            timeoutId = setTimeout(() => {
              setSimMessages(prev => [...prev, { sender: 'user', text: fullText }]);
              setSimUserText('');
              setSimStep(2);

              // Step 3: AI Typing Indicator
              timeoutId = setTimeout(() => {
                setSimStep(3); // Shows typing...

                // Step 4: AI Response
                timeoutId = setTimeout(() => {
                  setSimMessages(prev => [...prev, { sender: 'ai', text: script.aiResponse, showCta: true }]);
                  setSimStep(4);

                  // Step 5: Cursor appears & glides to CTA
                  timeoutId = setTimeout(() => {
                    setCursorOpacity(1);
                    // Coordinates relative to the chat window container
                    setCursorX(isRTL ? '22%' : '78%');
                    setCursorY('72%');
                    setSimStep(5);


                    // Step 6: Click effect
                    timeoutId = setTimeout(() => {
                      setIsCtaHovered(true);
                      setClickPulse(true);
                      setSimStep(6);

                      // Step 7: hide cursor
                      timeoutId = setTimeout(() => {
                        setClickPulse(false);
                        setIsCtaHovered(false);
                        setCursorOpacity(0);
                        setSimStep(7);
                        
                        // Animation effect for clicking login (actual redirect removed to prevent unwanted popups)

                        // Step 8: Pause and Restart
                        timeoutId = setTimeout(() => {
                          runSimulation();
                        }, 5000); // 5 seconds pause before loop starts again

                      }, 800); // Click duration

                    }, 1800); // Glide duration

                  }, 1200); // Delay before cursor appears

                }, 2000); // AI typing duration

              }, 800); // Delay before AI starts typing

            }, 800); // Delay after user typing finishes
          }
        }, 35); // Typing speed
      }, 2500);
    };

    runSimulation();

    return () => {
      clearTimeout(timeoutId);
      if (typeInterval) clearInterval(typeInterval);
    };
  }, [language, isRTL]);

  const footerLinks = [
    { label: language === 'tr' ? 'Gizlilik' : language === 'ar' ? 'الخصوصية' : 'Privacy', slug: 'privacy-policy' },
    { label: language === 'tr' ? 'Şartlar' : language === 'ar' ? 'الشروط' : 'Terms', slug: 'terms-of-service' },
    { label: language === 'tr' ? 'İade Politikası' : language === 'ar' ? 'سياسة الاسترداد' : 'Refund Policy', slug: 'refund-policy' },
    { label: language === 'tr' ? 'Çerezler' : language === 'ar' ? 'ملفات تعريف الارتباط' : 'Cookies', slug: 'cookie-policy' },
    { label: 'DPA', slug: 'dpa' },
    { label: language === 'tr' ? 'Yapay Zeka Politikası' : language === 'ar' ? 'سياسة الذكاء الاصطناعي' : 'AI Policy', slug: 'ai-policy' }
  ];

  // Get centralized content
  const content = getContent(language);

  // About section animation cycle
  useEffect(() => {
    const interval = setInterval(() => {
      setAboutAnimationStep((prev) => (prev + 1) % 3);
    }, 4000); // Change every 4 seconds
    return () => clearInterval(interval);
  }, []);

  // Learning section sliding animation cycle
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveLearningSlide((prev) => (prev + 1) % 3);
    }, 5000); // Change slide every 5 seconds
    return () => clearInterval(interval);
  }, []);

  // Job search typewriter effect
  useEffect(() => {
    const queries = [language === 'tr' ? 'Frontend Mühendisi' : language === 'ar' ? 'مهندس واجهات أمامية' : 'Frontend Engineer', 'UI/UX Designer', 'Product Manager'];
    const sources = ['linkedin', 'indeed', 'google'];
    let queryIdx = 0;
    let charIdx = 0;
    let isDeleting = false;
    let timer;

    const handleType = () => {
      const currentQuery = queries[queryIdx];
      if (!isDeleting) {
        setJobSearchQuery(currentQuery.substring(0, charIdx + 1));
        charIdx++;
        if (charIdx === currentQuery.length) {
          setActiveJobSource(sources[queryIdx]);
          timer = setTimeout(() => {
            isDeleting = true;
            handleType();
          }, 2200);
          return;
        }
      } else {
        setJobSearchQuery(currentQuery.substring(0, charIdx - 1));
        charIdx--;
        if (charIdx === 0) {
          isDeleting = false;
          queryIdx = (queryIdx + 1) % queries.length;
        }
      }
      timer = setTimeout(handleType, isDeleting ? 40 : 80);
    };

    handleType();
    return () => clearTimeout(timer);
  }, []);

  // Cover letter writer typewriter effect
  useEffect(() => {
    let timeoutId;
    let typeInterval;
    
    const letterEN = (language === 'tr' ? 'Sayın İşe Alım Yöneticisi,' : language === 'ar' ? 'عزيزي مدير التوظيف،' : 'Dear Hiring Manager,') + '\n\n' + (language === 'tr' ? 'Kıdemli Geliştirici rolü için başvurmaktan heyecan duyuyorum. Yüksek performanslı web uygulamaları oluşturma deneyimim, Bytelogs\'taki hedeflerinizle mükemmel bir şekilde örtüşüyor.' : language === 'ar' ? 'يسعدني التقدم لوظيفة مطور أول. خبرتي في بناء تطبيقات ويب عالية الأداء تتوافق تماماً مع أهدافكم في Bytelogs.' : 'I am thrilled to apply for the Senior Developer role. My experience building high-performance web applications aligns perfectly with your goals at Bytelogs.');
    const letterAR = "السادة أعضاء فريق التوظيف،\n\nيسعدني جداً التقدم لوظيفة مطور برمجيات أول. خبرتي الكبيرة في بناء واجهات المستخدم فائقة الأداء تتطابق تماماً مع متطلبات شركة Bytelogs.";
    
    const textToType = language === 'ar' ? letterAR : letterEN;
    
    const runTypingLoop = () => {
      setCoverLetterStep(0);
      setCoverLetterText('');
      
      timeoutId = setTimeout(() => {
        setCoverLetterStep(1);
        let currentText = '';
        let charIdx = 0;
        
        typeInterval = setInterval(() => {
          if (charIdx < textToType.length) {
            currentText += textToType[charIdx];
            setCoverLetterText(currentText);
            charIdx++;
          } else {
            clearInterval(typeInterval);
            setCoverLetterStep(2);
            
            timeoutId = setTimeout(() => {
              runTypingLoop();
            }, 6000);
          }
        }, 25);
      }, 1500);
    };
    
    runTypingLoop();
    
    return () => {
      clearTimeout(timeoutId);
      if (typeInterval) clearInterval(typeInterval);
    };
  }, [language]);

  // Recruiter ATS scan simulator effect
  useEffect(() => {
    let timeoutId;
    let scoreInterval;

    const runRecruiterSim = () => {
      setRecruiterStep(0);
      setRecruiterMatchScore(45);

      timeoutId = setTimeout(() => {
        setRecruiterStep(1);
        let currentScore = 45;
        scoreInterval = setInterval(() => {
          if (currentScore < 98) {
            currentScore += 3;
            setRecruiterMatchScore(currentScore);
          } else {
            clearInterval(scoreInterval);
            setRecruiterStep(2);

            timeoutId = setTimeout(() => {
              runRecruiterSim();
            }, 6000);
          }
        }, 80);
      }, 1500);
    };

    runRecruiterSim();

    return () => {
      clearTimeout(timeoutId);
      if (scoreInterval) clearInterval(scoreInterval);
    };
  }, []);

  // CV Builder live workspace simulator effect
  useEffect(() => {
    let timeoutId;
    let typeInterval;
    
    const summaryEN = language === 'tr' ? 'Duyarlı React uygulamaları oluşturmada 3+ yıllık deneyime sahip sonuç odaklı Frontend Geliştiricisi.' : language === 'ar' ? 'مطور واجهات أمامية يركز على النتائج بخبرة تزيد عن 3 سنوات في بناء تطبيقات React متجاوبة.' : 'Results-driven Frontend Developer with 3+ years of experience building responsive React applications.';
    const summaryAR = "مطور واجهات أمامية طموح ولديه خبرة تزيد عن 3 سنوات في بناء وتصميم تطبيقات React المتجاوبة.";
    const targetSummary = language === 'ar' ? summaryAR : summaryEN;
    
    const runCvBuilderSim = () => {
      setCvBuilderStep(0);
      setCvSummaryText('');
      setCvSkillsList([]);
      
      timeoutId = setTimeout(() => {
        setCvBuilderStep(1); // Writing summary
        let currentText = '';
        let charIdx = 0;
        
        typeInterval = setInterval(() => {
          if (charIdx < targetSummary.length) {
            currentText += targetSummary[charIdx];
            setCvSummaryText(currentText);
            charIdx++;
          } else {
            clearInterval(typeInterval);
            setCvBuilderStep(2); // Adding skills
            
            timeoutId = setTimeout(() => {
              setCvSkillsList(['React', 'Node.js', 'Framer Motion']);
              setCvBuilderStep(3); // Completed
              
              timeoutId = setTimeout(() => {
                runCvBuilderSim();
              }, 6000);
            }, 1500);
          }
        }, 30);
      }, 1500);
    };
    
    runCvBuilderSim();
    
    return () => {
      clearTimeout(timeoutId);
      if (typeInterval) clearInterval(typeInterval);
    };
  }, [language]);

  const openLegalModal = (slug) => {
    setRequestedLegalSlug(slug);
    setIsLegalModalOpen(true);
  };

  // Removed redirect - allow both logged-in and logged-out users to access home page

  const getMockHeader = () => {
    switch (activeMockTab) {
      case 'cv':
        return {
          title: language === 'ar' ? 'مساعد السيرة الذاتية الذكي' : 'AI Resume Workspace',
          desc: language === 'ar' ? 'تحديث تلقائي مستمر بالذكاء الاصطناعي' : 'Continuous real-time optimization'
        };
      case 'profile':
        return {
          title: language === 'ar' ? 'الملف الشخصي المهني' : 'Professional Profile',
          desc: language === 'ar' ? 'إدارة البيانات الأساسية والمهارات' : 'Manage personal details & skills'
        };
      case 'jobs':
        return {
          title: language === 'ar' ? 'فرص العمل المطابقة' : 'Matched Job Opportunities',
          desc: language === 'ar' ? 'وظائف مقترحة تناسب خبراتك وسيرتك' : 'AI matched job recommendations'
        };
      case 'ai':
        return {
          title: language === 'ar' ? 'المساعد المهني الذكي' : 'AI Career Assistant',
          desc: language === 'ar' ? 'محادثة ذكية لتحسين سيرتك والاستعداد للمقابلات' : 'Chat with your 24/7 AI Career Coach'
        };
      case 'target':
        return {
          title: language === 'ar' ? 'تحليلات السوق والأهداف' : 'Market Targets & Analytics',
          desc: language === 'ar' ? 'تتبع الشركات المستهدفة وقوة منافستك' : 'Track target companies & market competitiveness'
        };
      case 'tools':
        return {
          title: language === 'ar' ? 'أدوات التخصيص والإعدادات' : 'Customization Tools',
          desc: language === 'ar' ? 'إعدادات السيرة الذاتية واللغة والتصدير' : 'Resume settings, language, and export formats'
        };
      default:
        return { title: '', desc: '' };
    }
  };

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    // TODO: Implement newsletter subscription
    console.log('Newsletter subscription:', email);
    setEmail('');
  };

  return (
    <>
      <Helmet>
        <title>{content.hero.headline} - CVLogs</title>
        <meta name="description" content={content.hero.subheadline} />
        <meta property="og:title" content={`${content.hero.headline} - CVLogs`} />
        <meta property="og:description" content={content.hero.subheadline} />
      </Helmet>

      <LandingNavbar />

      <main className="min-h-screen" dir={isRTL ? 'rtl' : 'ltr'} role="main">
        {/* Ensure full width on mobile */}
        <style>
          {`
            @media (max-width: 640px) {
              main, html {
                overflow-x: hidden;
                width: 100%;
                margin: 0;
                padding: 0;
              }
            }
          `}
        </style>
        
        {/* Hero Section */}
        <section id="hero" aria-labelledby="hero-heading" className="relative mt-10 md:mt-2 min-h-screen flex items-center justify-center bg-white overflow-hidden">
          {/* Ambient Glow Blobs */}
          <div className="absolute top-1/4 left-10 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl pointer-events-none"></div>
          <div className="absolute bottom-1/4 right-10 w-[400px] h-[400px] bg-purple-500/5 rounded-full blur-3xl pointer-events-none"></div>

          {/* Grid Background - constrained to content area */}
          <div className="absolute inset-0 overflow-hidden">
            <div
              className="absolute inset-0 px-4 sm:px-6 lg:px-8"
              style={{
                backgroundSize: '20px 20px',
                backgroundImage: 'linear-gradient(to right, #e4e4e7 1px, transparent 1px), linear-gradient(to bottom, #e4e4e7 1px, transparent 1px)'
              }}
            />
          </div>

          {/* Radial gradient overlay - constrained to content area */}
          <div
            className="absolute inset-0 px-4 sm:px-6 lg:px-8 pointer-events-none bg-white"
            style={{
              maskImage: 'radial-gradient(ellipse at center, transparent 20%, black)',
              WebkitMaskImage: 'radial-gradient(ellipse at center, transparent 20%, black)'
            }}
          />

          <div className="relative z-10 w-full px-4 sm:px-6 lg:px-8 py-16 sm:py-24 md:py-32">
            <div className="max-w-7xl mx-auto">
              <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                {/* Left Content */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                  className="space-y-8 lg:space-y-10"
                >
               

                  {/* Main Heading */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3, duration: 0.6 }}
                  >
                    <h1 id="hero-heading" className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-[1.2] mb-6">
                      <span className="text-slate-900 block mb-2">
                        {content.hero.headline}
                      </span>
                      <span className="text-blue-600 block bg-gradient-to-r from-blue-600 via-blue-500 to-blue-600 bg-clip-text text-transparent pb-1">
                        {content.hero.headlineHighlight}
                      </span>
                    </h1>
                    <p className="text-sm sm:text-base lg:text-lg text-slate-600 leading-relaxed font-normal max-w-2xl">
                      {content.hero.subheadline}
                    </p>
                  </motion.div>

                  {/* CTA Buttons */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4, duration: 0.6 }}
                    className="flex flex-col sm:flex-row gap-4"
                  >
                    <Link
                      to={currentUser ? "/cv-builder" : "/register"}
                      className="group inline-flex items-center justify-center px-8 py-4 font-bold text-base rounded-xl transition-all duration-300 text-white shadow-lg hover:shadow-blue-500/25 bg-blue-600 hover:bg-blue-700 transform hover:scale-[1.02] hover:-translate-y-0.5"
                    >
                      <FileText className={`${isRTL ? 'ml-2' : 'mr-2'} h-5 w-5`} />
                      <span>{currentUser ? content.cta.ctaPrimaryUser : content.hero.ctaPrimary}</span>
                      <ArrowRight className={`${isRTL ? 'mr-2 group-hover:-translate-x-1' : 'ml-2 group-hover:translate-x-1'} h-5 w-5 transition-transform`} />
                    </Link>
                    
                    <Link
                      to="/job-search"
                      className="group inline-flex items-center justify-center px-8 py-4 border-2 border-slate-200 font-semibold text-base rounded-xl transition-all duration-300 bg-white hover:bg-slate-50 text-slate-900 hover:text-blue-600 hover:border-blue-300 transform hover:scale-[1.02] hover:-translate-y-0.5"
                    >
                      <Search className={`${isRTL ? 'ml-2' : 'mr-2'} h-5 w-5`} />
                      {content.hero.ctaSecondary}
                      <ChevronRight className={`${isRTL ? 'mr-2 group-hover:-translate-x-1' : 'ml-2 group-hover:translate-x-1'} h-4 w-4 transition-transform`} />
                    </Link>
                  </motion.div>

                  {/* Trust Indicators */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5, duration: 0.6 }}
                    className="flex flex-wrap items-center gap-6 pt-4"
                  >
                    <div className="flex items-center gap-2 text-slate-600">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="text-sm font-medium">Free to Start</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-600">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="text-sm font-medium">No Credit Card</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-600">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="text-sm font-medium">2 Min Setup</span>
                    </div>
                  </motion.div>
                </motion.div>

                {/* Right Visual */}
                <motion.div
                  initial={{ opacity: 0, x: isRTL ? -50 : 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                  className="relative hidden lg:flex items-center justify-center h-[650px] w-full max-w-[620px]"
                >
                  {/* Floating badge 1: Top float (Hired / Trophy) */}
                  <motion.div
                    animate={{ y: [-6, 6, -6] }}
                    transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                    className="absolute -top-4 right-8 z-30 bg-white/95 backdrop-blur-md border border-slate-200/80 rounded-2xl p-4 shadow-xl flex items-center gap-3 hover:scale-105 transition-transform duration-300"
                  >
                    <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center border border-amber-500/20">
                      <Award className="w-5 h-5 text-amber-500" />
                    </div>
                    <div>
                      <div className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider">
                        {language === 'ar' ? 'أحدث الإنجازات' : 'Latest Milestone'}
                      </div>
                      <div className="text-xs font-bold text-slate-800">
                        {language === 'ar' ? 'تم القبول في Bytelogs! 🎉' : 'Hired at Bytelogs! 🎉'}
                      </div>
                    </div>
                  </motion.div>

                  {/* Floating badge 2: Center-left float (Graduation / Skills) */}
                  <motion.div
                    animate={{ y: [6, -6, 6] }}
                    transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
                    className="absolute top-1/4 -left-6 z-30 bg-white/95 backdrop-blur-md border border-slate-200/80 rounded-2xl p-3.5 shadow-xl flex items-center gap-3 hover:scale-105 transition-transform duration-300"
                  >
                    <div className="w-9 h-9 rounded-full bg-blue-500 flex items-center justify-center shadow-lg shadow-blue-500/20">
                      <BookOpen className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <div className="text-xs font-bold text-slate-800">
                        {language === 'tr' ? 'Başvurmaya Hazır' : language === 'tr' ? 'Başvurmaya Hazır' : language === 'ar' ? 'جاهز للتسليم' : 'Ready to Apply'}
                      </div>
                    </div>
                  </motion.div>

                  {/* Main Dashboard Container */}
                  <div className="relative w-full bg-slate-900/5 backdrop-blur-md rounded-2xl border border-slate-200/80 shadow-2xl p-1 overflow-hidden bg-white flex">
                    {/* Vertical Sidebar */}
                    <div className="w-[68px] bg-slate-50 border-r border-slate-100 flex flex-col items-center py-6 gap-6">
                      <button 
                        onClick={() => setActiveMockTab('cv')}
                        className={`w-10 h-10 rounded-xl flex items-center justify-center font-extrabold text-sm transition-all duration-200 ${
                          activeMockTab === 'cv' 
                            ? 'bg-blue-600 text-white shadow-md shadow-blue-600/20' 
                            : 'bg-slate-200/60 text-slate-600 hover:bg-slate-200'
                        }`}
                      >
                        CV
                      </button>
                      
                      <button 
                        onClick={() => setActiveMockTab('profile')}
                        className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-200 ${
                          activeMockTab === 'profile' 
                            ? 'bg-blue-600 text-white shadow-md shadow-blue-600/20' 
                            : 'bg-slate-200/60 text-slate-600 hover:bg-slate-200'
                        }`}
                      >
                        <User className="w-5 h-5" />
                      </button>
                      
                      <button 
                        onClick={() => setActiveMockTab('jobs')}
                        className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-200 ${
                          activeMockTab === 'jobs' 
                            ? 'bg-blue-600 text-white shadow-md shadow-blue-600/20' 
                            : 'bg-slate-200/60 text-slate-600 hover:bg-slate-200'
                        }`}
                      >
                        <Briefcase className="w-5 h-5" />
                      </button>

                      <button 
                        onClick={() => setActiveMockTab('ai')}
                        className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-200 relative ${
                          activeMockTab === 'ai' 
                            ? 'bg-blue-600 text-white shadow-md shadow-blue-600/20' 
                            : 'bg-blue-50 text-blue-600 border border-blue-100 hover:bg-blue-100'
                        }`}
                      >
                        <Sparkles className="w-5 h-5" />
                        {activeMockTab !== 'ai' && (
                          <>
                            <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-blue-500 rounded-full animate-ping"></span>
                            <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-blue-500 rounded-full"></span>
                          </>
                        )}
                      </button>

                      <button 
                        onClick={() => setActiveMockTab('target')}
                        className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-200 ${
                          activeMockTab === 'target' 
                            ? 'bg-blue-600 text-white shadow-md shadow-blue-600/20' 
                            : 'bg-slate-200/60 text-slate-600 hover:bg-slate-200'
                        }`}
                      >
                        <Target className="w-5 h-5" />
                      </button>

                      <button 
                        onClick={() => setActiveMockTab('tools')}
                        className={`mt-auto w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-200 ${
                          activeMockTab === 'tools' 
                            ? 'bg-blue-600 text-white shadow-md shadow-blue-600/20' 
                            : 'bg-slate-200/60 text-slate-500 hover:bg-slate-200'
                        }`}
                      >
                        <span className="text-xs font-bold">🛠️</span>
                      </button>
                    </div>

                    {/* Dashboard Workspace */}
                    <div className="flex-1 flex flex-col min-w-0">
                      {/* Browser Header Bar */}
                      <div className="h-12 border-b border-slate-100 flex items-center justify-between px-6 bg-slate-50/50">
                        {/* Red, Yellow, Green Window Controls */}
                        <div className="flex items-center gap-2">
                          <span className="w-3 h-3 rounded-full bg-[#ff5f56]" />
                          <span className="w-3 h-3 rounded-full bg-[#ffbd2e]" />
                          <span className="w-3 h-3 rounded-full bg-[#27c93f]" />
                        </div>
                        {/* Mock Address Bar */}
                        <div className="text-[11px] text-slate-400 font-mono bg-white px-4 py-1 border border-slate-200/80 rounded-md shadow-sm w-44 text-center truncate">
                          {language === 'tr' ? 'cvlogs.ai/kontrol-paneli' : language === 'ar' ? 'cvlogs.ai/لوحة-القيادة' : 'cvlogs.ai/dashboard'}
                        </div>
                        <div className="w-10" />
                      </div>

                      {/* Content Area */}
                      <div className="p-6 flex flex-col gap-5 flex-1 overflow-y-auto">
                        
                        {/* Dashboard Top Header (Title & Active User) */}
                        <div className="flex items-center justify-between gap-4">
                          <div>
                            <h4 className="text-base font-bold text-slate-800">
                              {getMockHeader().title}
                            </h4>
                            <p className="text-[11px] text-slate-400 font-medium mt-0.5">
                              {getMockHeader().desc}
                            </p>
                          </div>
                          
                          {/* Active user header block */}
                          <div className="flex items-center gap-2 bg-slate-50 border border-slate-100 rounded-full px-3 py-1">
                            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                            <span className="text-[10px] font-bold text-slate-600">
                              {language === 'ar' ? 'متصل الآن' : 'Active'}
                            </span>
                          </div>
                        </div>

                        {/* Middle Row Layout */}
                        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-stretch min-h-[220px]">
                          {activeMockTab === 'cv' && (
                            <>
                              {/* CV Strength & Quick Stats Column (5 cols) */}
                              <div className="md:col-span-5 flex flex-col gap-4">
                                <div className="bg-gradient-to-br from-blue-50/50 via-white to-indigo-50/30 border border-slate-100 rounded-xl p-4 flex flex-col items-center text-center shadow-sm hover:shadow-md transition-shadow duration-300">
                                  <span className="text-[10.5px] font-bold text-slate-600 mb-3 tracking-wide">
                                    {language === 'tr' ? 'Özgeçmiş Güç Seviyesi' : language === 'tr' ? 'Özgeçmiş Güç Seviyesi' : language === 'ar' ? 'مستوى قوة السيرة الذاتية' : 'Resume Strength Level'}
                                  </span>
                                  <div className="relative w-24 h-24 flex items-center justify-center">
                                    <svg className="w-full h-full transform -rotate-90">
                                      <circle cx="48" cy="48" r="40" stroke="#f1f5f9" strokeWidth="8" fill="transparent" />
                                      <circle cx="48" cy="48" r="40" stroke="#3b82f6" strokeWidth="8" fill="transparent" strokeDasharray="251.2" strokeDashoffset="12.5" strokeLinecap="round" className="drop-shadow-[0_2px_8px_rgba(59,130,246,0.3)]" />
                                    </svg>
                                    <div className="absolute flex flex-col items-center justify-center">
                                      <span className="text-xl font-black text-slate-800">95%</span>
                                    </div>
                                  </div>
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                  <div className="bg-white border border-slate-100 rounded-xl p-3 text-center shadow-sm hover:border-blue-200 transition-colors">
                                    <div className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-1">
                                      {language === 'tr' ? 'ATS Notu' : language === 'tr' ? 'ATS Notu' : language === 'ar' ? 'تقييم أنظمة الفرز' : 'ATS Grade'}
                                    </div>
                                    <div className="text-base font-extrabold text-blue-600 bg-blue-50/50 py-1 rounded-lg">A+</div>
                                  </div>
                                  <div className="bg-white border border-slate-100 rounded-xl p-3 text-center shadow-sm hover:border-green-200 transition-colors">
                                    <div className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-1">
                                      {language === 'ar' ? 'التوافق المهني' : 'Match Rate'}
                                    </div>
                                    <div className="text-base font-extrabold text-green-600 bg-green-50/50 py-1 rounded-lg">98%</div>
                                  </div>
                                </div>
                              </div>

                              {/* Editor Preview Column (7 cols) */}
                              <div className="md:col-span-7 flex flex-col">
                                <div className="bg-white border border-slate-100 rounded-xl p-4 flex-1 flex flex-col shadow-sm">
                                  <div className="flex items-center justify-between mb-3 border-b border-slate-50 pb-2">
                                    <span className="text-xs font-bold text-slate-700">
                                      {language === 'tr' ? 'Canlı Özgeçmiş Önizlemesi' : language === 'tr' ? 'Canlı Özgeçmiş Önizlemesi' : language === 'ar' ? 'معاينة السيرة الذاتية' : 'Live Resume Preview'}
                                    </span>
                                    <div className="flex items-center gap-1.5 bg-red-500/10 border border-red-500/20 text-red-600 rounded-full px-2 py-0.5 text-[9px] font-black tracking-wider uppercase">
                                      <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-ping"></span>
                                      <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>
                                      {language === 'ar' ? 'مباشر' : 'Live Edit'}
                                    </div>
                                  </div>

                                  <div className="space-y-3 flex-1 flex flex-col justify-between mt-1">
                                    <div className="flex items-center gap-3">
                                      <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-extrabold text-sm shadow-md shadow-blue-500/25">
                                        أ
                                      </div>
                                      <div>
                                        <div className="text-xs font-extrabold text-slate-800">
                                          {language === 'tr' ? 'Ahmed Abdelrahman' : language === 'ar' ? 'أحمد عبد الرحمن' : 'Ahmed Abdelrahman'}
                                        </div>
                                        <div className="text-[9px] text-slate-400 font-semibold">
                                          {language === 'ar' ? 'مهندس برمجيات أول' : language === 'tr' ? 'Kıdemli Yazılım Mühendisi' : 'Senior Software Engineer'}
                                        </div>
                                      </div>
                                    </div>

                                    <div className="bg-slate-50/80 border border-slate-100 rounded-lg p-3 text-[10px] leading-relaxed text-slate-600 space-y-1">
                                      <div className="font-bold text-slate-800 flex items-center gap-1">
                                        <span className="text-blue-500">✨</span>
                                        {language === 'ar' ? 'اقتراح المساعد الذكي:' : 'AI Suggestion:'}
                                      </div>
                                      <p className="text-slate-500 text-[9px] italic bg-white p-2 border border-slate-100/50 rounded">
                                        {language === 'ar' 
                                          ? '"تمت صياغة خبراتك السابقة لتبرز قيادة الفرق وتحسين الأداء بنسبة 40%"'
                                          : '"Enhanced work experience section to highlight team leadership and 40% performance gains."'}
                                      </p>
                                    </div>

                                    <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden mt-1">
                                      <div className="h-full bg-blue-600 w-4/5 rounded-full animate-pulse" />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </>
                          )}

                          {activeMockTab === 'profile' && (
                            <>
                              <div className="md:col-span-5 flex flex-col gap-4">
                                <div className="bg-gradient-to-br from-emerald-50/50 via-white to-green-50/30 border border-slate-100 rounded-xl p-4 flex flex-col items-center text-center shadow-sm hover:shadow-md transition-shadow duration-300">
                                  <span className="text-[10.5px] font-bold text-slate-600 mb-3 tracking-wide">
                                    {language === 'ar' ? 'اكتمال الملف الشخصي' : 'Profile Completion'}
                                  </span>
                                  <div className="relative w-24 h-24 flex items-center justify-center">
                                    <svg className="w-full h-full transform -rotate-90">
                                      <circle cx="48" cy="48" r="40" stroke="#f1f5f9" strokeWidth="8" fill="transparent" />
                                      <circle cx="48" cy="48" r="40" stroke="#10b981" strokeWidth="8" fill="transparent" strokeDasharray="251.2" strokeDashoffset="37.6" strokeLinecap="round" className="drop-shadow-[0_2px_8px_rgba(16,185,129,0.3)]" />
                                    </svg>
                                    <div className="absolute flex flex-col items-center justify-center">
                                      <span className="text-xl font-black text-slate-800">85%</span>
                                    </div>
                                  </div>
                                </div>
                                <div className="bg-white border border-slate-100 rounded-xl p-3 text-center shadow-sm">
                                  <div className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">
                                    {language === 'ar' ? 'الخبرة العملية المكتشفة' : language === 'tr' ? 'Algılanan İş Deneyimi' : 'Detected Work Experience'}
                                  </div>
                                  <div className="text-xs font-bold text-slate-700">{language === 'ar' ? '5 سنوات ونصف' : '5.5 Years'}</div>
                                </div>
                              </div>

                              <div className="md:col-span-7 flex flex-col">
                                <div className="bg-white border border-slate-100 rounded-xl p-4 flex-1 flex flex-col shadow-sm justify-between">
                                  <span className="text-xs font-bold text-slate-700 border-b border-slate-50 pb-2 mb-2 block">
                                    {language === 'ar' ? 'المعلومات الأساسية والمهارات' : 'Basic Info & Key Skills'}
                                  </span>
                                  <div className="space-y-3 flex-1 flex flex-col justify-between">
                                    <div className="grid grid-cols-2 gap-2.5 text-[9.5px] bg-slate-50 border border-slate-200/50 p-2.5 rounded-lg text-slate-600 font-medium">
                                      <div><span className="text-slate-400">{language === 'tr' ? 'İsim:' : language === 'tr' ? 'İsim:' : language === 'ar' ? 'الاسم:' : 'Name:'}</span> <br/><span className="text-slate-800 font-bold">{language === 'ar' ? 'أحمد عبد الرحمن' : 'Ahmed Abdelrahman'}</span></div>
                                      <div><span className="text-slate-400">{language === 'ar' ? 'البريد:' : 'Email:'}</span> <br/><span className="text-slate-800 font-bold">contact@cvlogs.com</span></div>
                                      <div className="col-span-2 border-t border-slate-200/50 pt-1.5"><span className="text-slate-400">{language === 'ar' ? 'المسمى والتركيز:' : 'Focus:'}</span> <br/><span className="text-blue-600 font-bold">{language === 'ar' ? 'مطور واجهات تفاعلية أول' : 'Senior Interactive Frontend Dev'}</span></div>
                                    </div>
                                    <div className="flex flex-wrap gap-1 mt-1">
                                      {['React.js', 'TypeScript', 'TailwindCSS', 'REST APIs'].map((skill, index) => (
                                        <span key={index} className="text-[9px] bg-blue-50 text-blue-600 border border-blue-100 rounded px-2 py-0.5 font-extrabold hover:bg-blue-100 transition-colors">
                                          {skill}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </>
                          )}

                          {activeMockTab === 'jobs' && (
                            <>
                              <div className="md:col-span-5 flex flex-col gap-4">
                                <div className="bg-white border border-slate-100 rounded-xl p-4 text-center shadow-sm">
                                  <span className="text-[10.5px] font-bold text-slate-700 block mb-3 border-b border-slate-50 pb-2">
                                    {language === 'ar' ? 'طلبات التوظيف النشطة' : 'Active Job Pipeline'}
                                  </span>
                                  <div className="space-y-2">
                                    <div className="flex justify-between items-center text-[10px]">
                                      <div className="flex items-center gap-1.5">
                                        <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                                        <span className="text-slate-500">{language === 'ar' ? 'المرحلة النهائية' : 'Shortlisted'}</span>
                                      </div>
                                      <span className="font-extrabold text-slate-800 bg-slate-100 px-2 py-0.5 rounded">3</span>
                                    </div>
                                    <div className="flex justify-between items-center text-[10px]">
                                      <div className="flex items-center gap-1.5">
                                        <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                                        <span className="text-slate-500">{language === 'ar' ? 'تحت المراجعة' : 'Under Review'}</span>
                                      </div>
                                      <span className="font-extrabold text-slate-800 bg-slate-100 px-2 py-0.5 rounded">5</span>
                                    </div>
                                  </div>
                                </div>
                                <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200/50 rounded-xl p-3 text-center">
                                  <div className="text-[9px] text-green-800 font-bold uppercase tracking-wider mb-0.5">
                                    {language === 'ar' ? 'نسبة مطابقة الوظائف' : 'Average Profile Match'}
                                  </div>
                                  <div className="text-base font-extrabold text-green-600">94%</div>
                                </div>
                              </div>

                              <div className="md:col-span-7 flex flex-col">
                                <div className="bg-white border border-slate-100 rounded-xl p-4 flex-1 flex flex-col shadow-sm justify-between">
                                  <span className="text-xs font-bold text-slate-700 border-b border-slate-50 pb-2 mb-2 block">
                                    {language === 'ar' ? 'وظائف موصى بها لك' : 'AI Recruited Job Matches'}
                                  </span>
                                  <div className="space-y-2 mt-1 flex-1 justify-center flex flex-col">
                                    {[
                                      { title: language === 'ar' ? 'مطور واجهات أول - Bytelogs' : 'Senior Frontend Developer', match: '98%', company: 'Bytelogs Ltd', location: 'Remote' },
                                      { title: language === 'ar' ? 'مهندس React - Google' : 'React Engineer', match: '94%', company: 'Google Inc', location: 'Egypt' },
                                    ].map((job, index) => (
                                      <div key={index} className="bg-slate-50 hover:bg-slate-100/80 border border-slate-200/50 p-2.5 rounded-lg flex items-center justify-between gap-3 text-[10px] transition-colors">
                                        <div>
                                          <div className="font-bold text-slate-800 leading-tight">{job.title}</div>
                                          <div className="text-[8.5px] text-slate-400 mt-0.5">{job.company} • {job.location}</div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <span className="text-[9px] px-2 py-0.5 bg-green-50 text-green-700 font-extrabold rounded-md border border-green-100">{job.match}</span>
                                          <button className="bg-blue-600 hover:bg-blue-700 transition-colors text-white font-bold text-[8.5px] px-2.5 py-1 rounded">
                                            {language === 'ar' ? 'تقديم' : 'Apply'}
                                          </button>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </>
                          )}

                          {activeMockTab === 'ai' && (
                            <>
                              <div className="md:col-span-5 flex flex-col gap-4">
                                <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm hover:border-blue-200 transition-colors duration-300">
                                  <span className="text-[10.5px] font-bold text-slate-700 block mb-2.5 tracking-wide">
                                    {language === 'ar' ? 'مهام سريعة بالذكاء الاصطناعي' : 'Quick AI Assistants'}
                                  </span>
                                  <div className="space-y-1.5">
                                    {(language === 'ar' 
                                      ? ['تعديل الخبرات فورياً', 'توليد رسالة تغطية مخصصة', 'اقتراح مهارات ناقصة'] 
                                      : ['Instantly Optimize Resume', 'Generate Cover Letter', 'Suggest Missing Skills']
                                    ).map((act, index) => (
                                      <button key={index} className="w-full text-left rtl:text-right bg-slate-50 hover:bg-blue-50/50 hover:text-blue-600 border border-slate-200/60 text-[9px] text-slate-600 p-2 rounded-lg transition-colors font-bold flex items-center gap-1">
                                        <span>⚡</span> {act}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              </div>

                              <div className="md:col-span-7 flex flex-col">
                                <div className="bg-white border border-slate-100 rounded-xl p-3 flex-1 flex flex-col shadow-sm justify-between">
                                  <span className="text-xs font-bold text-slate-700 mb-2 border-b border-slate-50 pb-2">
                                    {language === 'ar' ? 'مستشارك المهني الافتراضي' : 'AI Career Assistant Chat'}
                                  </span>
                                  
                                  {/* Chat Content Panel */}
                                  <div className="space-y-2 mt-1 text-[9px] flex-1 overflow-y-auto max-h-[110px] pr-1 flex flex-col gap-1">
                                    <div className="bg-slate-100 text-slate-700 p-2 rounded-lg max-w-[85%] rounded-tl-none self-start leading-relaxed">
                                      {language === 'ar' 
                                        ? 'مرحباً أحمد! كيف يمكنني مساعدتك اليوم في تحسين سيرتك الذاتية؟' 
                                        : 'Hi Ahmed! How can I help you optimize your resume today?'}
                                    </div>
                                    <div className="bg-blue-600 text-white p-2 rounded-lg max-w-[85%] rounded-tr-none self-end text-right leading-relaxed shadow-sm shadow-blue-500/10">
                                      {language === 'ar' 
                                        ? 'ممكن تحسن سطر الخبرة الأخير في السيرة الذاتية؟' 
                                        : 'Can you optimize my last experience line?'}
                                    </div>
                                    <div className="bg-slate-100 text-slate-700 p-2 rounded-lg max-w-[85%] rounded-tl-none self-start leading-relaxed">
                                      {language === 'ar' 
                                        ? 'بالتأكيد! أقترح الصياغة: "قيادة فريق تطوير الواجهات لبناء منصات ويب سريعة مع تحسين الأداء بنسبة 40%."'
                                        : 'Sure! I suggest: "Led frontend team to build responsive web apps, improving performance by 40%."'}
                                    </div>
                                  </div>

                                  {/* Chat Input simulator */}
                                  <div className="mt-2.5 pt-2 border-t border-slate-100 flex items-center justify-between gap-1.5">
                                    <input 
                                      type="text" 
                                      disabled
                                      placeholder={language === 'ar' ? 'اكتب رسالتك للمساعد الذكي...' : 'Type message here...'} 
                                      className="bg-slate-50 border border-slate-100 text-[8.5px] rounded-lg px-2 py-1.5 flex-1 outline-none text-slate-400 cursor-not-allowed font-medium"
                                    />
                                    <button disabled className="bg-blue-600/80 text-white p-1 px-2.5 rounded-lg text-[8.5px] font-bold cursor-not-allowed">
                                      {language === 'ar' ? 'إرسال' : 'Send'}
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </>
                          )}

                          {activeMockTab === 'target' && (
                            <>
                              <div className="md:col-span-5 flex flex-col gap-4">
                                <div className="bg-gradient-to-br from-indigo-50/50 via-white to-blue-50/30 border border-slate-100 rounded-xl p-4 flex flex-col items-center text-center shadow-sm hover:shadow-md transition-shadow duration-300">
                                  <span className="text-[10.5px] font-bold text-slate-600 mb-2 tracking-wide">
                                    {language === 'ar' ? 'قوة التنافسية بالسوق' : 'Competitiveness Index'}
                                  </span>
                                  <div className="relative w-24 h-24 flex items-center justify-center">
                                    <svg className="w-full h-full transform -rotate-90">
                                      <circle cx="48" cy="48" r="40" stroke="#f1f5f9" strokeWidth="8" fill="transparent" />
                                      <circle cx="48" cy="48" r="40" stroke="#4f46e5" strokeWidth="8" fill="transparent" strokeDasharray="251.2" strokeDashoffset="20.1" strokeLinecap="round" className="drop-shadow-[0_2px_8px_rgba(79,70,229,0.3)]" />
                                    </svg>
                                    <div className="absolute flex flex-col items-center justify-center">
                                      <span className="text-xl font-black text-slate-800">92%</span>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <div className="md:col-span-7 flex flex-col">
                                <div className="bg-white border border-slate-100 rounded-xl p-4 flex-1 flex flex-col shadow-sm justify-between">
                                  <span className="text-xs font-bold text-slate-700 border-b border-slate-50 pb-2 mb-2 block">
                                    {language === 'ar' ? 'تحليلات السوق المستهدف والظهور' : 'Target Market Analytics'}
                                  </span>
                                  <div className="space-y-2 mt-2 flex-1 justify-center flex flex-col">
                                    <div className="bg-slate-50 border border-slate-200/60 p-3 rounded-lg space-y-2 text-[9.5px] text-slate-600 font-medium">
                                      <div className="flex justify-between items-center">
                                        <span className="text-slate-400">{language === 'ar' ? 'الشركات المستهدفة المضافة:' : 'Target companies:'}</span>
                                        <span className="font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full border border-indigo-100/50">8 شركات</span>
                                      </div>
                                      <div className="flex justify-between items-center border-t border-slate-200/50 pt-2">
                                        <span className="text-slate-400">{language === 'ar' ? 'معدل ظهور ملفك للشركات:' : 'Profile impressions:'}</span>
                                        <span className="font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded-full border border-green-100/50">+150%</span>
                                      </div>
                                      <div className="text-[8.5px] text-slate-400 border-t border-slate-200/50 pt-2">
                                        <span className="font-semibold text-slate-500">{language === 'ar' ? 'المهارات المطلوبة بالسوق:' : 'Market in-demand skills:'}</span><br/>
                                        <span className="text-indigo-600 font-bold">Next.js, TailwindCSS, AI APIs</span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </>
                          )}

                          {activeMockTab === 'tools' && (
                            <>
                              <div className="md:col-span-5 flex flex-col gap-4">
                                <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm text-center">
                                  <span className="text-[10.5px] font-bold text-slate-700 block mb-3 border-b border-slate-50 pb-2">
                                    {language === 'ar' ? 'ملخص الإعدادات النشطة' : 'Setup Summary'}
                                  </span>
                                  <div className="space-y-2 text-[10px] text-slate-500 font-semibold">
                                    <div className="flex justify-between items-center">
                                      <span>{language === 'ar' ? 'قوالب مفعلة' : 'Active Templates'}</span>
                                      <span className="font-bold text-slate-800 bg-slate-100 px-2 py-0.5 rounded">2</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                      <span>{language === 'ar' ? 'صيغة التصدير الافتراضية' : 'Default Export'}</span>
                                      <span className="font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded border border-blue-100">PDF</span>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <div className="md:col-span-7 flex flex-col">
                                <div className="bg-white border border-slate-100 rounded-xl p-4 flex-1 flex flex-col shadow-sm justify-between">
                                  <span className="text-xs font-bold text-slate-700 border-b border-slate-50 pb-2 mb-2 block">
                                    {language === 'ar' ? 'خيارات تخصيص السيرة الذاتية' : 'CV Customization Options'}
                                  </span>
                                  <div className="space-y-2 mt-2 flex-1 justify-center flex flex-col">
                                    <div className="flex items-center justify-between bg-slate-50 border border-slate-200/50 p-2 px-3 rounded-lg text-[9.5px]">
                                      <span className="text-slate-500 font-semibold">{language === 'ar' ? 'لغة التوليد التلقائي' : 'AI Generation Language'}</span>
                                      <span className="text-blue-600 font-bold bg-blue-50/50 px-2 py-0.5 rounded border border-blue-100/50">{language === 'ar' ? 'العربية' : 'Arabic'}</span>
                                    </div>
                                    <div className="flex items-center justify-between bg-slate-50 border border-slate-200/50 p-2 px-3 rounded-lg text-[9.5px]">
                                      <span className="text-slate-500 font-semibold">{language === 'ar' ? 'قالب السيرة الذاتية' : 'Resume Template'}</span>
                                      <span className="text-blue-600 font-bold bg-blue-50/50 px-2 py-0.5 rounded border border-blue-100/50">{language === 'ar' ? 'الكلاسيكي الحديث' : 'Modern Classic'}</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </>
                          )}
                        </div>

                        {/* Bottom Full-Width CTA */}
                        <div className="bg-slate-900 border border-slate-800 text-white rounded-xl p-3.5 flex items-center justify-between gap-4 mt-auto">
                          <div className="flex items-center gap-3">
                            <span className="text-lg">📄</span>
                            <div>
                              <div className="text-[11px] font-bold">
                                {language === 'ar' ? 'سيرتك الذاتية جاهزة الآن' : 'Your Professional CV is Ready'}
                              </div>
                              <div className="text-[9px] text-slate-400">
                                {language === 'ar' ? 'متوافقة مع أنظمة الفرز (ATS)' : 'Fully Optimized for ATS filters'}
                              </div>
                            </div>
                          </div>
                          <button className="bg-blue-600 hover:bg-blue-700 transition-colors text-white font-bold text-[10px] px-3.5 py-1.5 rounded-lg">
                            {language === 'tr' ? 'Raporu Görüntüle' : language === 'tr' ? 'Raporu Görüntüle' : language === 'ar' ? 'عرض التقرير' : 'View Report'}
                          </button>
                        </div>

                      </div>
                    </div>
                  </div>

                  {/* Floating badge 3: Bottom float (Skills Chart / Material list) */}
                  <motion.div
                    animate={{ y: [6, -6, 6] }}
                    transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut" }}
                    className="absolute -bottom-6 right-16 z-30 bg-white/95 backdrop-blur-md border border-slate-200/80 rounded-2xl p-4 shadow-xl w-44 hover:scale-105 transition-transform duration-300"
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-xs">📊</span>
                      <span className="text-[10px] font-bold text-slate-700">
                        {language === 'tr' ? 'Yetenek Eşleşmesi' : language === 'tr' ? 'Yetenek Eşleşmesi' : language === 'ar' ? 'المهارات المطلوبة' : 'Skills Match'}
                      </span>
                    </div>
                    {/* Visual bar graph representation */}
                    <div className="flex items-end justify-between h-10 px-2 gap-1.5">
                      <div className="w-2.5 bg-slate-200 rounded-t h-1/2"></div>
                      <div className="w-2.5 bg-blue-600 rounded-t h-4/5"></div>
                      <div className="w-2.5 bg-blue-600 rounded-t h-full"></div>
                      <div className="w-2.5 bg-blue-500 rounded-t h-3/5"></div>
                      <div className="w-2.5 bg-slate-200 rounded-t h-1/4"></div>
                    </div>
                  </motion.div>
                </motion.div>
              </div>
            </div>
          </div>
        </section>

        {/* About Section */}
        <section id="features" className="py-24 px-4 sm:px-6 lg:px-8 bg-slate-50 overflow-hidden">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
              style={{ minHeight: '400px' }}
            >
              <AnimatePresence mode="wait">
                {aboutAnimationStep === 0 && (
                  <motion.div
                    key="step1"
                    initial={{ opacity: 0, scale: 0.9, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.9, y: -20 }}
                    transition={{ duration: 0.6 }}
                    className="absolute inset-0 flex flex-col items-center justify-center"
                  >
                    <motion.div
                      animate={{ 
                        scale: [1, 1.1, 1],
                        rotate: [0, 5, -5, 0]
                      }}
                      transition={{ duration: 2, repeat: Infinity, repeatDelay: 2 }}
                      className="mb-8"
                    >
                      <div className="w-24 h-24 bg-blue-600 rounded-2xl flex items-center justify-center shadow-2xl">
                        <FileText className="w-12 h-12 text-white" />
                      </div>
                    </motion.div>
                    <h2 className="text-4xl md:text-5xl font-black text-slate-900 mb-6">
                      {content.about.title}
                    </h2>
                    <p className="text-xl text-slate-600 leading-relaxed max-w-2xl">
                      {content.about.description}
                    </p>
                  </motion.div>
                )}

                {aboutAnimationStep === 1 && (
                  <motion.div
                    key="step2"
                    initial={{ opacity: 0, scale: 0.9, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.9, y: -20 }}
                    transition={{ duration: 0.6 }}
                    className="absolute inset-0 flex flex-col items-center justify-center"
                  >
                    <div className="grid grid-cols-3 gap-6 mb-8">
                      {[
                        { icon: FileText, label: language === 'ar' ? 'إنشاء السيرة' : 'Build CV', color: 'blue' },
                        { icon: Target, label: language === 'ar' ? 'البحث عن وظائف' : 'Find Jobs', color: 'green' },
                        { icon: Sparkles, label: language === 'ar' ? 'ذكاء اصطناعي' : 'AI Power', color: 'purple' }
                      ].map((item, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.2 }}
                          className="flex flex-col items-center"
                        >
                          <motion.div
                            animate={{ y: [-5, 5, -5] }}
                            transition={{ duration: 2, repeat: Infinity, delay: index * 0.3 }}
                            className={`w-16 h-16 bg-${item.color}-100 rounded-xl flex items-center justify-center mb-3 border-2 border-${item.color}-300`}
                          >
                            <item.icon className={`w-8 h-8 text-${item.color}-600`} />
                          </motion.div>
                          <p className="text-sm font-bold text-slate-700">{item.label}</p>
                        </motion.div>
                      ))}
                    </div>
                    <h3 className="text-3xl font-black text-slate-900 mb-4">
                      {language === 'ar' ? 'كل ما تحتاجه في مكان واحد' : 'Everything You Need in One Place'}
                    </h3>
                    <p className="text-lg text-slate-600 max-w-2xl">
                      {language === 'ar' 
                        ? 'منصة متكاملة تجمع بين إنشاء السيرة الذاتية والبحث عن الوظائف والذكاء الاصطناعي'
                        : 'A complete platform combining CV creation, job search, and AI assistance'
                      }
                    </p>
                  </motion.div>
                )}

                {aboutAnimationStep === 2 && (
                  <motion.div
                    key="step3"
                    initial={{ opacity: 0, scale: 0.9, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.9, y: -20 }}
                    transition={{ duration: 0.6 }}
                    className="absolute inset-0 flex flex-col items-center justify-center"
                  >
                    
                    <h3 className="text-3xl font-black text-slate-900 mb-4">
                      {language === 'ar' ? 'رؤيتنا ومهمتنا' : 'Our Vision & Mission'}
                    </h3>
                    <motion.div
                      animate={{ 
                        scale: [1, 1.05, 1]
                      }}
                      transition={{ duration: 2, repeat: Infinity }}
                      className="inline-flex items-center gap-3 px-8 py-2 bg-blue-600 rounded-2xl mb-4 shadow-2xl"
                    >
                      <Heart className="w-8 h-8 text-white" />
                      <span className="text-2xl font-black text-white">{content.about.mission}</span>
                      <motion.div
                        animate={{ 
                          scale: [1, 1.3, 1],
                          opacity: [0.5, 1, 0.5]
                        }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                        className="w-3 h-3 bg-white rounded-full"
                      />
                    </motion.div>
                    <p className="text-lg text-slate-600 max-w-2xl">
                      {language === 'ar'
                        ? 'نؤمن بأن كل شخص يستحق فرصة عادلة للنجاح في حياته المهنية'
                        : 'We believe everyone deserves a fair chance to succeed in their career'
                      }
                    </p>
                    
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Progress Dots */}
              <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 flex gap-2">
                {[0, 1, 2].map((step) => (
                  <motion.button
                    key={step}
                    onClick={() => setAboutAnimationStep(step)}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${
                      aboutAnimationStep === step ? 'bg-blue-600 w-8' : 'bg-slate-300'
                    }`}
                    whileHover={{ scale: 1.2 }}
                    whileTap={{ scale: 0.9 }}
                    aria-label={`Go to step ${step + 1}`}
                  />
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Before vs After Slider Section */}
        <section id="before-after-slider" className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-950 border-y border-slate-900 overflow-hidden relative" dir="ltr">
          {/* Ambient Glows */}
          <div className="absolute top-10 left-1/4 w-72 h-72 bg-blue-500/20 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute bottom-10 right-1/4 w-80 h-80 bg-emerald-500/20 rounded-full blur-3xl pointer-events-none" />

          <div className="max-w-6xl mx-auto relative z-10">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="text-xs font-bold text-blue-400 bg-blue-950/40 border border-blue-800/30 px-3 py-1 rounded-full uppercase tracking-wider animate-pulse">
                {language === 'ar' ? 'مقارنة حية تفاعلية' : 'Interactive Live Comparison'}
              </span>
              <h2 className="text-3xl sm:text-5xl font-black text-white mt-3 mb-4 tracking-tight leading-tight">
                {content.beforeAfter.title}
              </h2>
              <p className="text-slate-400 text-sm sm:text-lg max-w-xl mx-auto font-medium">
                {content.beforeAfter.subtitle}
              </p>
            </div>

            {/* Slider Container with Hover Lift */}
            <motion.div 
              whileHover={{ y: -6, scale: 1.015 }}
              transition={{ type: 'spring', stiffness: 300, damping: 22 }}
              className="relative w-full max-w-4xl mx-auto bg-slate-900 rounded-3xl shadow-[0_25px_60px_rgba(0,0,0,0.25)] border-4 border-slate-800 overflow-hidden select-none p-1 sm:p-2"
            >
              <div className="relative aspect-[16/10] w-full rounded-2xl overflow-hidden bg-slate-100 shadow-inner">
                
                {/* BEFORE PANEL (Messy, traditional resume, low score) */}
                <div 
                  className={`absolute inset-0 w-full h-full bg-[#FCFAF7] p-8 flex flex-col justify-between overflow-y-auto ${isRTL ? 'text-right' : 'text-left'} font-serif z-10`}
                  dir={isRTL ? 'rtl' : 'ltr'}
                >
                  {/* Crumpled paper texture effect overlay */}
                  <div className="absolute inset-0 opacity-[0.04] pointer-events-none bg-repeat bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-amber-900 via-transparent to-transparent" />

                  {/* Red Sticky Marker Notes */}
                  <div className={`absolute top-20 ${isRTL ? 'left-8' : 'right-8'} z-20 bg-red-100/95 backdrop-blur-sm border-2 border-red-300 text-red-800 text-[8px] sm:text-[10px] font-bold px-3 py-1.5 rounded-lg shadow-md font-sans ${isRTL ? '-rotate-6' : 'rotate-6'} max-w-[120px] sm:max-w-[150px] leading-tight`}>
                    📌 {language === 'ar' ? 'تصميم مزدحم وغير منظم!' : 'Cluttered structure!'}
                  </div>

                  <div className={`absolute bottom-20 ${isRTL ? 'left-8' : 'right-8'} z-20 bg-red-100/95 backdrop-blur-sm border-2 border-red-300 text-red-800 text-[8px] sm:text-[10px] font-bold px-3 py-1.5 rounded-lg shadow-md font-sans ${isRTL ? 'rotate-3' : '-rotate-3'} max-w-[120px] sm:max-w-[160px] leading-tight`}>
                    📌 {language === 'ar' ? 'لا توجد أرقام أو قياس للإنجازات!' : 'No quantified metrics!'}
                  </div>

                  {/* MS Word Header Look (Spatially aligned with After header) */}
                  <div className="border-b border-slate-200 pb-4 relative z-10">
                    <h3 className="text-xl sm:text-3xl font-bold text-slate-800 tracking-wide font-serif line-through decoration-red-500/80">
                      {content.beforeAfter.beforeTitle}
                    </h3>
                    <div className={`text-[9px] text-slate-500 font-sans mt-1.5 flex flex-wrap gap-2 items-center ${isRTL ? 'justify-start' : ''}`}>
                      <span className="text-red-650 font-extrabold">{content.beforeAfter.beforeSub}</span>
                      <span>•</span>
                      <span>{isRTL ? 'القاهرة، مصر' : 'Cairo, Egypt'}</span>
                      <span>•</span>
                      <span>{content.beforeAfter.beforeScore}</span>
                    </div>
                  </div>

                  {/* Bad Resume Content layout (Spatially aligned with After content) */}
                  <div className="my-5 space-y-5 text-xs text-slate-700 font-serif leading-relaxed relative z-10 max-w-2xl w-full">
                    <div className="space-y-1.5">
                      <span className="text-[9px] font-bold text-slate-400 font-sans tracking-wider block">{language === 'tr' ? 'ÖZET' : language === 'ar' ? 'الملخص' : 'SUMMARY'}</span>
                      <p className={`p-3 bg-red-50/30 border border-red-100 rounded-xl text-slate-650 italic ${isRTL ? 'text-right' : 'text-left'}`}>
                        {content.beforeAfter.beforeSummary}
                      </p>
                    </div>

                    <div className="space-y-1.5">
                      <span className="text-[9px] font-bold text-slate-400 font-sans tracking-wider block">{content.beforeAfter.beforeExpTitle}</span>
                      <div className={`p-3 bg-red-50/30 border border-red-100 rounded-xl whitespace-pre-line text-slate-650 font-mono text-[9px] leading-relaxed ${isRTL ? 'text-right' : 'text-left'}`}>
                        {content.beforeAfter.beforeExpBody}
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-slate-200 pt-4 flex justify-between items-center text-[10px] font-sans font-bold text-slate-400 relative z-10">
                    <span>{isRTL ? 'صفحة 1 من 3 (طويل جداً)' : 'Page 1 of 3 (Too Long)'}</span>
                    <span className="text-red-600 bg-red-50 px-3 py-1 rounded-full border border-red-200/80 flex items-center gap-1">❌ {content.beforeAfter.beforeLabel}</span>
                  </div>
                </div>

                {/* AFTER PANEL (Clean, premium resume, high score) */}
                <div 
                  className={`absolute inset-0 w-full h-full bg-white p-8 flex flex-col justify-between overflow-y-auto ${isRTL ? 'text-right' : 'text-left'} font-sans select-none z-20`}
                  dir={isRTL ? 'rtl' : 'ltr'}
                  style={{
                    clipPath: isRTL 
                      ? `polygon(0 0, ${beforeAfterVal}% 0, ${beforeAfterVal}% 100%, 0 100%)` 
                      : `polygon(0 0, ${beforeAfterVal}% 0, ${beforeAfterVal}% 100%, 0 100%)`
                  }}
                >
                  {/* Decorative modern border glow */}
                  <div className="absolute top-0 left-0 right-0 h-[4px] bg-gradient-to-r from-blue-500 via-indigo-500 to-emerald-500" />
                  <div className="absolute top-0 right-0 w-40 h-40 bg-blue-500/5 rounded-full blur-3xl pointer-events-none" />

                  {/* Green Sticky Approval Notes */}
                  <div className={`absolute top-20 ${isRTL ? 'left-8' : 'right-8'} z-20 bg-emerald-100/95 backdrop-blur-sm border-2 border-emerald-300 text-emerald-800 text-[8px] sm:text-[10px] font-bold px-3 py-1.5 rounded-lg shadow-md font-sans ${isRTL ? '-rotate-3' : 'rotate-3'} max-w-[120px] sm:max-w-[160px] leading-tight`}>
                    ✓ {language === 'ar' ? 'تنسيق ATS مثالي وصور مصغرة!' : 'Perfect ATS styling!'}
                  </div>

                  {/* Premium PDF Header Look (Spatially aligned with Before header) */}
                  <div className="border-b border-slate-100 pb-4 relative z-10">
                    <div>
                      <h3 className="text-xl sm:text-3xl font-black text-slate-900 tracking-tight bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-600 bg-clip-text text-transparent">
                        {content.beforeAfter.afterTitle}
                      </h3>
                      <div className={`text-[9px] text-slate-400 font-bold mt-1.5 flex flex-wrap gap-2 items-center ${isRTL ? 'justify-start' : ''}`}>
                        <span className="text-blue-600 font-extrabold">{content.beforeAfter.afterSub}</span>
                        <span>•</span>
                        <span>{isRTL ? 'القاهرة، مصر' : 'Cairo, Egypt'}</span>
                        <span>•</span>
                        <span className="bg-emerald-50 text-emerald-700 border border-emerald-200 px-2.5 py-0.5 rounded text-[9.5px] font-extrabold">{content.beforeAfter.afterScore}</span>
                      </div>
                    </div>
                  </div>

                  {/* Premium Content layout (Spatially aligned with Before content) */}
                  <div className="my-5 space-y-5 text-xs text-slate-700 leading-relaxed relative z-10 max-w-2xl w-full">
                    <div className="space-y-1.5">
                      <span className="text-[9px] font-extrabold text-blue-600 tracking-wider uppercase block">{language === 'tr' ? 'Profesyonel Özet' : language === 'ar' ? 'الملخص المهني' : 'Professional Summary'}</span>
                      <p className={`p-3 bg-gradient-to-br from-blue-50/20 to-indigo-50/20 border border-blue-100/50 rounded-xl font-medium text-slate-800 shadow-sm leading-relaxed ${isRTL ? 'text-right' : 'text-left'}`}>
                        {content.beforeAfter.afterSummary}
                      </p>
                    </div>

                    <div className="space-y-1.5">
                      <span className="text-[9px] font-extrabold text-blue-600 tracking-wider uppercase block">{content.beforeAfter.afterExpTitle}</span>
                      <div className={`p-3 bg-gradient-to-br from-blue-50/20 to-indigo-50/20 border border-blue-100/50 rounded-xl whitespace-pre-line text-slate-850 font-medium leading-relaxed ${isRTL ? 'text-right' : 'text-left'}`}>
                        {content.beforeAfter.afterExpBody}
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-slate-100 pt-4 flex justify-between items-center text-[10px] font-bold text-slate-400 relative z-10">
                    <span className="text-blue-600 bg-blue-50 px-3 py-1 rounded-md border border-blue-100">1 Page (Optimal)</span>
                    <span className="text-emerald-700 bg-emerald-50 px-3 py-1 rounded-full border border-emerald-200/80 flex items-center gap-1">✨ {content.beforeAfter.afterLabel}</span>
                  </div>
                </div>

                {/* DIVIDER LINE & HANDLE */}
                <div 
                  className="absolute top-0 bottom-0 w-1 bg-white cursor-ew-resize shadow-[0_0_20px_rgba(0,0,0,0.4)] z-30 pointer-events-none"
                  style={{
                    left: `${beforeAfterVal}%`
                  }}
                >
                  <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-12 h-12 rounded-full bg-blue-600 text-white shadow-[0_0_25px_rgba(37,99,235,0.7)] flex items-center justify-center border-4 border-white pointer-events-none transition-transform duration-200 scale-100 group-hover:scale-110">
                    <span className="text-base font-extrabold animate-pulse">↔</span>
                  </div>
                </div>

                {/* INPUT RANGE (Absolute Overlay covering the container) */}
                <input 
                  type="range" 
                  min="0" 
                  max="100" 
                  value={beforeAfterVal} 
                  onChange={(e) => setBeforeAfterVal(Number(e.target.value))}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-ew-resize z-40"
                  style={{ direction: 'ltr' }}
                  aria-label="Before/After CV Slider"
                />

              </div>
            </motion.div>
          </div>
        </section>

        {/* CV Builder Section */}
        <section id="cv-builder" className="py-24 px-4 sm:px-6 lg:px-8 bg-white relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-1/2 left-10 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl" />
          </div>

          <div className="max-w-7xl mx-auto relative z-10">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              
              {/* Left Column: Text & Features */}
              <motion.div
                initial={{ opacity: 0, x: isRTL ? 50 : -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="space-y-8"
              >
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-200 rounded-full">
                  <Sparkles className="w-4 h-4 text-blue-600 animate-pulse" />
                  <span className="text-sm font-semibold text-blue-600">
                    {language === 'ar' ? 'منشئ السيرة الذاتية الذكي' : 'AI Resume Builder'}
                  </span>
                </div>
                
                <h2 className="text-4xl md:text-5xl font-black text-slate-900 leading-tight">
                  {content.cvBuilder.title}
                </h2>
                <p className="text-lg text-slate-600 leading-relaxed">
                  {content.cvBuilder.description}
                </p>
                
                <ul className="space-y-4">
                  {content.cvBuilder.features.map((feature, index) => (
                    <motion.li 
                      key={index}
                      whileHover={{ x: isRTL ? -4 : 4 }}
                      className="flex items-start gap-3 p-1 rounded-lg hover:bg-slate-50 transition-colors"
                    >
                      <CheckCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700 font-semibold">{feature}</span>
                    </motion.li>
                  ))}
                </ul>

                <Link
                  to="/cv-builder"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-[1.02]"
                >
                  {content.cvBuilder.cta}
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </motion.div>

              {/* Right Column: Interactive AI CV Creator Simulator Workspace */}
              <motion.div
                initial={{ opacity: 0, x: isRTL ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="relative bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-2xl overflow-hidden min-h-[440px] flex flex-col justify-between"
              >
                
                {/* Mock Browser Header & Controls */}
                <div className="flex items-center justify-between pb-4 border-b border-slate-800">
                  <div className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-500/80" />
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/80" />
                    <div className="w-2.5 h-2.5 rounded-full bg-green-500/80" />
                    <span className="text-[10px] font-mono text-slate-500 ml-2">{language === 'tr' ? 'cvlogs.ai/ozgecmis-olusturucu' : language === 'ar' ? 'cvlogs.ai/منشئ-السيرة' : 'cvlogs.ai/resume-creator'}</span>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                    <span className="text-[10px] font-bold text-blue-400">
                      {language === 'ar' ? 'محرر السيرة نشط' : language === 'tr' ? 'Canlı CV Düzenleyici' : 'CV Editor Live'}
                    </span>
                  </div>
                </div>

                {/* Workspace layout: Section Selectors & Live Document Preview */}
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4 my-5 items-stretch">
                  
                  {/* Left Sidebar sections (4 cols) */}
                  <div className="md:col-span-4 bg-slate-950/60 border border-slate-850 rounded-xl p-3 flex flex-col gap-2">
                    {[
                      { id: 0, label: language === 'ar' ? 'البيانات الشخصية' : language === 'tr' ? 'Kişisel Bilgiler' : 'Personal Info', active: false },
                      { id: 1, label: language === 'ar' ? 'الملخص المهني' : 'Professional Summary', active: cvBuilderStep === 1 },
                      { id: 2, label: language === 'ar' ? 'الخبرات السابقة' : 'Work Experience', active: false },
                      { id: 3, label: language === 'ar' ? 'المهارات التقنية' : language === 'tr' ? 'Beceriler ve Araçlar' : 'Skills & Tools', active: cvBuilderStep >= 2 }
                    ].map((sec) => (
                      <div
                        key={sec.id}
                        className={`text-[9.5px] font-bold px-2 py-1.5 rounded transition-all duration-300 ${
                          sec.active 
                            ? 'bg-blue-600/10 text-blue-400 border border-blue-500/30' 
                            : 'text-slate-500 bg-slate-900/40 border border-slate-800/40'
                        }`}
                      >
                        {sec.label}
                      </div>
                    ))}
                  </div>

                  {/* Right: Live Resume Document preview (8 cols) */}
                  <div className="md:col-span-8 bg-slate-950 border border-slate-850 rounded-xl p-4 min-h-[220px] flex flex-col justify-between relative overflow-hidden">
                    <div className="absolute top-1.5 right-2 flex items-center gap-1">
                      <span className={`w-1.5 h-1.5 rounded-full ${cvBuilderStep === 3 ? 'bg-green-500' : 'bg-blue-500 animate-ping'}`} />
                      <span className="text-[8px] text-slate-500 font-bold uppercase tracking-wider">
                        {cvBuilderStep === 3 
                          ? (language === 'ar' ? 'جاهز للتنزيل' : 'Ready to Download') 
                          : (language === 'ar' ? 'جاري تحسين التنسيق...' : 'Formatting CV...')
                        }
                      </span>
                    </div>

                    {/* Document Header preview */}
                    <div className="space-y-3 flex-1 flex flex-col justify-start mt-2">
                      <div className="border-b border-slate-800 pb-2">
                        <h4 className="text-xs font-black text-slate-100">
                          {language === 'ar' ? 'أحمد عبد الرحمن' : 'Ahmed Abdelrahman'}
                        </h4>
                        <p className="text-[9px] text-slate-400 font-medium">{language === 'ar' ? 'مهندس برمجيات' : 'Software Engineer'}</p>
                      </div>

                      {/* Summary Section */}
                      <div className="space-y-1">
                        <span className="text-[8px] text-slate-500 font-bold uppercase tracking-widest block">
                          {language === 'ar' ? 'الملخص' : 'Summary'}
                        </span>
                        <p className="text-[9.5px] text-slate-350 leading-relaxed font-mono min-h-[50px]">
                          {cvSummaryText}
                          {cvBuilderStep === 1 && (
                            <span className="inline-block w-1 h-3 bg-blue-500 ml-0.5 animate-pulse" />
                          )}
                        </p>
                      </div>

                      {/* Skills Section */}
                      {cvSkillsList.length > 0 && (
                        <div className="space-y-1">
                          <span className="text-[8px] text-slate-500 font-bold uppercase tracking-widest block">
                            {language === 'ar' ? 'المهارات' : 'Skills'}
                          </span>
                          <div className="flex flex-wrap gap-1.5">
                            {cvSkillsList.map((skill, idx) => (
                              <motion.span
                                key={idx}
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                className="text-[8.5px] font-bold px-2 py-0.5 rounded bg-blue-500/10 text-blue-400 border border-blue-500/20"
                              >
                                {skill}
                              </motion.span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Download controller */}
                    <div className="border-t border-slate-850/60 pt-3 mt-2 flex justify-between items-center">
                      <span className="text-[8.5px] text-slate-500 font-bold">PDF • DOCX</span>
                      <button
                        disabled={cvBuilderStep !== 3}
                        className={`text-[9px] font-black uppercase tracking-wider px-3 py-1.5 rounded-lg border transition-all duration-300 ${
                          cvBuilderStep === 3
                            ? 'bg-blue-600 text-white border-blue-500 shadow-md shadow-blue-500/15 hover:scale-105 hover:bg-blue-500'
                            : 'bg-slate-900 border-slate-800 text-slate-650 cursor-not-allowed'
                        }`}
                      >
                        {language === 'ar' ? 'تحميل السيرة الذاتية 📥' : 'Download CV 📥'}
                      </button>
                    </div>
                  </div>

                </div>

                {/* Progress Indicators */}
                <div className="flex justify-center gap-1.5 pt-3 border-t border-slate-800">
                  {[0, 1, 2, 3].map((stepIdx) => (
                    <span
                      key={stepIdx}
                      className={`h-1.5 rounded-full transition-all duration-300 ${
                        (cvBuilderStep === stepIdx) ? 'w-6 bg-blue-500' : 'w-1.5 bg-slate-800'
                      }`}
                    />
                  ))}
                </div>
              </motion.div>

            </div>
          </div>
        </section>

      {/* Interactive CV Builder Preview Section */}
        <section id="how-it-works" className="py-24 bg-slate-900 relative overflow-hidden">
          <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.2)' }}></div>
          <div className="absolute inset-0 px-4 sm:px-6 lg:px-8">
            <div className="absolute top-0 left-1/4 w-72 h-72 rounded-full blur-3xl animate-pulse" style={{ background: 'rgba(59,130,246,0.15)' }}></div>
            <div className="absolute bottom-0 right-1/4 w-72 h-72 rounded-full blur-3xl animate-pulse" style={{ background: 'rgba(59,130,246,0.15)', animationDelay: '1s' }}></div>
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                {/* 24/7 Available Badge with Glow & Hover Animations */}
                <motion.div 
                  whileHover={{ scale: 1.05 }}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/20 rounded-full mb-4 shadow-[0_0_15px_rgba(59,130,246,0.1)] relative overflow-hidden group"
                >
                  <span className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-500/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                  <MessageSquare className="w-4 h-4 text-blue-400 animate-pulse" />
                  <span className="text-xs font-bold text-blue-400 tracking-wider">
                    {language === 'ar' ? 'مدربك المهني على مدار الساعة' : '24/7 Available Career Coach'}
                  </span>
                </motion.div>
                
                {/* Title with Gradient shifting effect */}
                <h2 className="text-3xl md:text-4xl font-extrabold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 via-indigo-200 to-blue-400 animate-pulse bg-[size:200%_auto] tracking-tight">
                  {language === 'ar' ? 'شاهد السحر يحدث' : language === 'tr' ? 'Sihrin Gerçekleşmesini İzleyin' : 'Watch the Magic Happen'}
                </h2>
              </motion.div>
            </div>


            {/* Animated AI Agent Simulation Workspace (Compact Height) */}
            <div className="relative bg-slate-900/60 border border-slate-700/40 rounded-2xl p-4 lg:p-6 shadow-2xl overflow-hidden backdrop-blur-md max-w-5xl mx-auto">
              
              {/* Window Controls Decorator */}
              <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-800/80">
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-500/80" />
                  <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/80" />
                  <div className="w-2.5 h-2.5 rounded-full bg-green-500/80" />
                  <span className="text-[10px] font-mono text-slate-500 ml-1.5">cvlogs-ai-agent.sh</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                  <span className="text-[10px] font-bold text-slate-400">
                    {language === 'ar' ? 'المساعد متصل' : language === 'tr' ? 'Asistan Aktif' : 'Agent Active'}
                  </span>
                </div>
              </div>

              <div className="grid lg:grid-cols-12 gap-6 items-stretch relative">
                
                {/* Column 1: AI Chat Simulator UI (7 cols - Compact) */}
                <div className="lg:col-span-7 flex flex-col justify-between bg-slate-950/70 border border-slate-800 rounded-xl p-4 h-[350px] relative">
                  
                  {/* Messages Area */}
                  <div className="space-y-3 flex-1 overflow-y-auto mb-3 pr-1 flex flex-col justify-end">
                    {simMessages.map((msg, idx) => (
                      <motion.div
                        key={idx}
                        initial={{ opacity: 0, y: 10, scale: 0.99 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{ duration: 0.3 }}
                        className={`flex flex-col max-w-[85%] ${
                          msg.sender === 'user' ? 'self-end items-end' : 'self-start items-start'
                        }`}
                      >
                        <span className="text-[9px] text-slate-500 mb-0.5 font-semibold px-1">
                          {msg.sender === 'user' 
                            ? (language === 'ar' ? 'أنت' : 'You')
                            : (language === 'ar' ? 'مساعد سيرة ذاتية الذكي' : 'CVlogs Assistant')
                          }
                        </span>
                        <div className={`p-3 rounded-xl text-xs leading-relaxed ${
                          msg.sender === 'user'
                            ? 'bg-blue-600 text-white rounded-tr-none shadow-md shadow-blue-600/10'
                            : 'bg-slate-800/80 text-slate-200 border border-slate-700/40 rounded-tl-none'
                        }`}>
                          <p className="whitespace-pre-line">{msg.text}</p>
                          
                          {/* Inside-chat CTA button that the cursor clicks */}
                          {msg.showCta && (
                            <div className="mt-3">
                              <button
                                onClick={() => {
                                  try {
                                    window.open('/login', '_blank');
                                  } catch (e) {
                                    console.error(e);
                                  }
                                }}
                                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg font-black text-[10px] uppercase tracking-wider transition-all duration-300 transform ${
                                  isCtaHovered
                                    ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-xl shadow-blue-500/25 scale-105 border-t border-white/20'
                                    : 'bg-blue-600 hover:bg-blue-500 text-white border-t border-blue-500/30'
                                }`}
                              >
                                {language === 'ar' ? 'جرب المساعد الآن مجاناً 🚀' : 'Try AI Assistant Free 🚀'}
                              </button>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    ))}

                    {/* Simulating AI is typing state */}
                    {simStep === 3 && (
                      <div className="self-start flex flex-col items-start max-w-[80%]">
                        <span className="text-[9px] text-slate-500 mb-0.5 font-semibold px-1">
                          {language === 'ar' ? 'المساعد يكتب...' : 'Assistant is typing...'}
                        </span>
                        <div className="bg-slate-800/80 border border-slate-700/40 p-3 rounded-xl rounded-tl-none flex gap-1 items-center">
                          <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                          <span className="w-1.5 h-1.5 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                          <span className="w-1.5 h-1.5 bg-blue-300 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Input Area Simulator */}
                  <div className="border-t border-slate-800/80 pt-3 flex items-center gap-2">
                    <div className="flex-1 bg-slate-900 border border-slate-800 rounded-lg px-3 py-2 text-xs flex items-center justify-between text-slate-400">
                      <span>
                        {simStep === 1 ? (
                          <>
                            {simUserText}
                            <span className="inline-block w-1.5 h-3.5 bg-blue-500 ml-0.5 animate-pulse" />
                          </>
                        ) : (
                          simStep > 1 ? '' : (language === 'ar' ? 'اكتب رسالتك للمساعد الذكي...' : 'Type message for career advisor...')
                        )}
                      </span>
                      <Sparkles className="w-3.5 h-3.5 text-blue-500/70" />
                    </div>
                    <button
                      disabled
                      className={`px-4 py-2 rounded-lg font-bold text-[10px] bg-slate-800 text-slate-500 border border-slate-700/50 cursor-not-allowed`}
                    >
                      {language === 'ar' ? 'إرسال' : 'Send'}
                    </button>
                  </div>

                </div>

                {/* Column 2: Dashboard Real-time preview (5 cols - Compact) */}
                <div className="lg:col-span-5 bg-slate-950/40 border border-slate-800/60 rounded-xl p-4 flex flex-col justify-between h-[350px]">
                  <div>
                    <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2.5 border-b border-slate-800/80 pb-1.5">
                      {language === 'ar' ? 'تقرير التوافق الفوري' : 'Live Optimization Report'}
                    </h4>

                    {/* Compact score dial */}
                    <div className="flex flex-col items-center justify-center py-3 bg-slate-900/40 border border-slate-800/50 rounded-xl mb-3 relative overflow-hidden">
                      <div className="relative w-24 h-24 flex items-center justify-center">
                        <svg className="w-full h-full transform -rotate-90">
                          {/* Background Track */}
                          <circle cx="48" cy="48" r="38" stroke="#1e293b" strokeWidth="6" fill="transparent" />
                          {/* Animated Progress Ring */}
                          <motion.circle
                            cx="48"
                            cy="48"
                            r="38"
                            stroke="#3b82f6"
                            strokeWidth="6"
                            fill="transparent"
                            strokeLinecap="round"
                            strokeDasharray="238.76"
                            initial={{ strokeDashoffset: 238.76 * (1 - 0.65) }}
                            animate={{
                              strokeDashoffset: simStep >= 4 ? 238.76 * (1 - 0.95) : 238.76 * (1 - 0.65)
                            }}
                            transition={{ duration: 1.5, ease: "easeOut" }}
                            className="drop-shadow-[0_0_8px_rgba(59,130,246,0.4)]"
                          />
                        </svg>
                        <div className="absolute flex flex-col items-center">
                          <span className="text-xl font-black text-white tracking-tight">
                            {simStep >= 4 ? '95%' : '65%'}
                          </span>
                          <span className="text-[8px] text-slate-500 font-bold uppercase">
                            {language === 'ar' ? 'قوة السيرة' : 'CV Strength'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Stats List */}
                    <div className="space-y-2">
                      <div className="bg-slate-900/50 border border-slate-800/60 rounded-lg p-2.5 flex justify-between items-center">
                        <span className="text-[10px] text-slate-400 font-medium">
                          {language === 'ar' ? 'تقييم أنظمة الفرز (ATS)' : 'ATS Score Grade'}
                        </span>
                        <span className={`text-[10px] font-black px-2 py-0.5 rounded transition-colors duration-500 ${
                          simStep >= 4 
                            ? 'bg-green-500/10 text-green-400 border border-green-500/20' 
                            : 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20'
                        }`}>
                          {simStep >= 4 ? 'A+' : 'C+'}
                        </span>
                      </div>

                      <div className="bg-slate-900/50 border border-slate-800/60 rounded-lg p-2.5 flex justify-between items-center">
                        <span className="text-[10px] text-slate-400 font-medium">
                          {language === 'ar' ? 'الكلمات المفتاحية المكتشفة' : language === 'tr' ? 'Belirlenen ATS Anahtar Kelimeleri' : 'ATS Keywords Identified'}
                        </span>
                        <span className="text-[10px] font-bold text-slate-200">
                          {simStep >= 4 ? '28 / 30' : '14 / 30'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Optimization tips indicator */}
                  <div className="mt-2.5 pt-2.5 border-t border-slate-800/80">
                    <p className="text-[10px] text-slate-500 font-medium leading-relaxed bg-slate-950/40 border border-slate-800/80 p-2.5 rounded-lg italic">
                      <span className="font-bold text-blue-400 block not-italic mb-0.5">
                        {language === 'ar' ? '✨ نصيحة المساعد التلقائي:' : language === 'tr' ? '✨ Canlı Öneri:' : '✨ Live Suggestion:'}
                      </span>
                      {simStep >= 4 
                        ? (language === 'ar' 
                          ? 'لقد تم تحسين صياغة المسميات وإضافة المهارات المهنية الناقصة تلقائياً.' 
                          : language === 'tr' ? 'Deneyimler başarıyla cilalandı, hedef beceri setleri eklendi ve eylem fiilleri artırıldı.' : 'Successfully polished experiences, added target skillsets, and boosted action verbs.')
                        : (language === 'ar' 
                          ? 'بانتظار تحليل السيرة الذاتية لإصدار التوصيات...' 
                          : 'Awaiting message to begin CV scans & content polish...')
                      }
                    </p>
                  </div>
                </div>

                {/* Simulated Floating Pointer Arrow */}
                <motion.div
                  className="absolute pointer-events-none z-50 select-none"
                  animate={{
                    left: cursorX,
                    top: cursorY,
                    opacity: cursorOpacity,
                  }}
                  transition={{
                    duration: 1.8,
                    ease: "easeInOut"
                  }}
                  style={{
                    x: '-50%',
                    y: '-50%',
                  }}
                >
                  <div className="relative">
                    {/* Ripple click effect */}
                    {clickPulse && (
                      <span className="absolute top-0 left-0 w-8 h-8 -translate-x-[20%] -translate-y-[20%] rounded-full bg-blue-500/40 animate-ping border border-blue-400" />
                    )}
                    {/* Sleek MacOS-style Cursor */}
                    <svg 
                      className={`w-7 h-7 drop-shadow-xl ${clickPulse ? 'scale-90' : 'scale-100'} transition-transform duration-200`} 
                      viewBox="0 0 24 24" 
                      fill="none"
                    >
                      <path 
                        d="M4 3.5V19.5L8.9 14.8L13.7 22.5L16.2 21L11.5 13.5L18 13L4 3.5Z" 
                        fill="white" 
                        stroke="#1e3a8a" 
                        strokeWidth="1.5" 
                        strokeLinejoin="round" 
                      />
                    </svg>
                  </div>
                </motion.div>

              </div>
            </div>
            
            {/* Action buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-center mt-16"
            >
              <Link
                to="/login"
                className="inline-flex items-center px-10 py-4 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-2xl hover:shadow-xl transform hover:scale-105 transition-all duration-300 group mr-4"
              >
                <span className='text-white'>{language === 'ar' ? 'جرب المساعد الآن' : language === 'tr' ? 'Asistanı Dene' : language === 'ar' ? 'جرب المساعد' : 'Try the Assistant'}</span>
                <ArrowRight className={`${isRTL ? 'mr-3 group-hover:-translate-x-1' : 'ml-3 group-hover:translate-x-1'} h-5 w-5 transition-transform`} />
              </Link>
              
              <button className="inline-flex items-center px-8 py-4 bg-slate-700/50 backdrop-blur-md text-slate-200 font-semibold rounded-2xl border border-slate-600/50 hover:bg-slate-700 transition-all duration-300">
                <Play className={`${isRTL ? 'ml-3' : 'mr-3'} h-5 w-5`} />
                {language === 'ar' ? 'شاهد الفيديو التوضيحي' : language === 'tr' ? 'Demoyu İzle' : language === 'ar' ? 'شاهد العرض' : 'Watch Demo'}
              </button>
            </motion.div>
          </div>
        </section>


          
        {/* Job Discovery Section */}
        <section id="job-discovery" className="py-24 px-4 sm:px-6 lg:px-8 bg-slate-50 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-1/4 left-10 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl" />
            <div className="absolute bottom-1/4 right-10 w-[400px] h-[400px] bg-purple-500/5 rounded-full blur-3xl" />
          </div>

          <div className="max-w-7xl mx-auto relative z-10">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              
              {/* Left Column: Interactive Job Simulator Dashboard */}
              <motion.div
                initial={{ opacity: 0, x: isRTL ? 50 : -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="relative bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-2xl overflow-hidden min-h-[440px] flex flex-col justify-between lg:order-1"
              >
                {/* Mock Browser Header & Controls */}
                <div className="flex items-center justify-between pb-4 border-b border-slate-800">
                  <div className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-500/80" />
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/80" />
                    <div className="w-2.5 h-2.5 rounded-full bg-green-500/80" />
                    <span className="text-[10px] font-mono text-slate-500 ml-2">{language === 'tr' ? 'cvlogs.ai/is-kesfi' : language === 'ar' ? 'cvlogs.ai/اكتشاف-الوظائف' : 'cvlogs.ai/job-discovery'}</span>
                  </div>
                  
                  {/* Job Platforms tabs */}
                  <div className="flex bg-slate-950/80 p-0.5 rounded-lg border border-slate-800">
                    {[
                      { id: 'linkedin', label: language === 'tr' ? 'LinkedIn' : language === 'ar' ? 'لينكد إن' : 'LinkedIn' },
                      { id: 'indeed', label: language === 'tr' ? 'Indeed' : language === 'ar' ? 'إنديد' : 'Indeed' },
                      { id: 'google', label: language === 'tr' ? 'Google Jobs' : language === 'ar' ? 'وظائف جوجل' : 'Google Jobs' }
                    ].map((platform) => (
                      <button
                        key={platform.id}
                        disabled
                        className={`text-[9px] font-bold px-2 py-1 rounded transition-all duration-300 ${
                          activeJobSource === platform.id 
                            ? 'bg-blue-600 text-white shadow-md' 
                            : 'text-slate-400'
                        }`}
                      >
                        {platform.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Animated Search Bar Workspace */}
                <div className="my-5 space-y-4">
                  <div className="bg-slate-950 border border-slate-850 rounded-xl p-3 flex items-center justify-between">
                    <div className="flex items-center gap-2.5 min-w-0 flex-1">
                      <Search className="w-4 h-4 text-blue-500 flex-shrink-0" />
                      <span className="text-xs text-slate-200 font-medium truncate">
                        {jobSearchQuery}
                        <span className="inline-block w-1 h-3.5 bg-blue-500 ml-0.5 animate-pulse" />
                      </span>
                    </div>
                    <span className="text-[9px] font-bold text-slate-500 uppercase bg-slate-900 border border-slate-800 px-2 py-0.5 rounded">
                      AI Auto-Search
                    </span>
                  </div>

                  {/* Mock Jobs Feed matches typing query */}
                  <div className="space-y-3 min-h-[195px] flex flex-col justify-center">
                    <AnimatePresence mode="wait">
                      {activeJobSource === 'linkedin' && (
                        <motion.div
                          key="linkedin-job"
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          transition={{ duration: 0.3 }}
                          className="bg-slate-950/80 border border-slate-850 p-4 rounded-xl flex items-center justify-between shadow-lg group hover:border-slate-700 cursor-pointer"
                        >
                          <div className="min-w-0 pr-2">
                            <div className="flex items-center gap-2 mb-1.5">
                              <span className="text-[9px] font-bold px-2 py-0.5 rounded bg-blue-500/10 text-blue-400 border border-blue-500/20">
                                LinkedIn
                              </span>
                              <span className="text-[9px] text-slate-400 font-bold">{language === 'tr' ? '%98 Eşleşme Oranı' : language === 'ar' ? 'نسبة تطابق 98%' : '98% Match Rate'}</span>
                            </div>
                            <h4 className="text-xs font-bold text-slate-200 truncate group-hover:text-white">
                              {language === 'ar' ? 'مهندس واجهات أمامية أول (React)' : language === 'tr' ? 'Kıdemli Frontend Mühendisi (React)' : language === 'ar' ? 'مهندس واجهات أمامية أول (React)' : 'Senior Frontend Engineer (React)'}
                            </h4>
                            <p className="text-[10px] text-slate-400 font-medium mt-1">
                              Bytelogs Inc. • {language === 'ar' ? 'عن بُعد' : 'Remote'} • $80k - $110k
                            </p>
                          </div>
                          <span className="w-8 h-8 rounded-lg bg-blue-600/10 border border-blue-500/20 flex items-center justify-center text-xs font-black text-blue-400 flex-shrink-0">
                            in
                          </span>
                        </motion.div>
                      )}

                      {activeJobSource === 'indeed' && (
                        <motion.div
                          key="indeed-job"
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          transition={{ duration: 0.3 }}
                          className="bg-slate-950/80 border border-slate-850 p-4 rounded-xl flex items-center justify-between shadow-lg group hover:border-slate-700 cursor-pointer"
                        >
                          <div className="min-w-0 pr-2">
                            <div className="flex items-center gap-2 mb-1.5">
                              <span className="text-[9px] font-bold px-2 py-0.5 rounded bg-indigo-500/10 text-indigo-400 border border-indigo-500/20">
                                Indeed
                              </span>
                              <span className="text-[9px] text-slate-400 font-bold">95% Match Rate</span>
                            </div>
                            <h4 className="text-xs font-bold text-slate-200 truncate group-hover:text-white">
                              {language === 'ar' ? 'مصمم تجربة مستخدم (Figma)' : 'Lead UI/UX Designer (Figma)'}
                            </h4>
                            <p className="text-[10px] text-slate-400 font-medium mt-1">
                              CreativeHub • {language === 'ar' ? 'عن بُعد' : 'Remote'} • $70k - $95k
                            </p>
                          </div>
                          <span className="w-8 h-8 rounded-lg bg-indigo-600/10 border border-indigo-500/20 flex items-center justify-center text-xs font-black text-indigo-400 flex-shrink-0">
                            Id
                          </span>
                        </motion.div>
                      )}

                      {activeJobSource === 'google' && (
                        <motion.div
                          key="google-job"
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          transition={{ duration: 0.3 }}
                          className="bg-slate-950/80 border border-slate-850 p-4 rounded-xl flex items-center justify-between shadow-lg group hover:border-slate-700 cursor-pointer"
                        >
                          <div className="min-w-0 pr-2">
                            <div className="flex items-center gap-2 mb-1.5">
                              <span className="text-[9px] font-bold px-2 py-0.5 rounded bg-green-500/10 text-green-400 border border-green-500/20">
                                Google Jobs
                              </span>
                              <span className="text-[9px] text-slate-400 font-bold">92% Match Rate</span>
                            </div>
                            <h4 className="text-xs font-bold text-slate-200 truncate group-hover:text-white">
                              {language === 'ar' ? 'مدير منتجات تقنية' : 'Technical Product Manager'}
                            </h4>
                            <p className="text-[10px] text-slate-400 font-medium mt-1">
                              TechCorp • {language === 'ar' ? 'القاهرة، مصر' : 'Cairo, Egypt'} • Competitive
                            </p>
                          </div>
                          <span className="w-8 h-8 rounded-lg bg-green-600/10 border border-green-500/20 flex items-center justify-center text-xs font-black text-green-400 flex-shrink-0">
                            G
                          </span>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>

                {/* Progress Indicators */}
                <div className="flex justify-center gap-1.5 pt-3 border-t border-slate-800">
                  {['linkedin', 'indeed', 'google'].map((source) => (
                    <span
                      key={source}
                      className={`h-1.5 rounded-full transition-all duration-300 ${
                        activeJobSource === source ? 'w-6 bg-blue-500' : 'w-1.5 bg-slate-800'
                      }`}
                    />
                  ))}
                </div>
              </motion.div>

              {/* Right Column: Text & Features */}
              <motion.div
                initial={{ opacity: 0, x: isRTL ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="space-y-8 lg:order-2"
              >
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-200 rounded-full">
                  <Target className="w-4 h-4 text-blue-600 animate-pulse" />
                  <span className="text-sm font-semibold text-blue-600">
                    {language === 'ar' ? 'المطابقة الذكية للوظائف' : 'Smart Job Matching'}
                  </span>
                </div>
                
                <h2 className="text-4xl md:text-5xl font-black text-slate-900 leading-tight">
                  {content.jobDiscovery.title} <span className="text-blue-600">{content.jobDiscovery.subtitle}</span>
                </h2>
                <p className="text-lg text-slate-600 leading-relaxed">
                  {content.jobDiscovery.description}
                </p>
                
                <ul className="space-y-4">
                  {content.jobDiscovery.features.map((feature, index) => (
                    <motion.li 
                      key={index}
                      whileHover={{ x: isRTL ? -4 : 4 }}
                      className="flex items-start gap-3 p-1 rounded-lg hover:bg-slate-100/50 transition-colors"
                    >
                      <CheckCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700 font-semibold">{feature}</span>
                    </motion.li>
                  ))}
                </ul>

                <Link
                  to="/job-search"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-[1.02]"
                >
                  {content.jobDiscovery.cta}
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </motion.div>

            </div>
          </div>
        </section>

        {/* Cover Letters Section */}
        <section id="cover-letters" className="py-24 px-4 sm:px-6 lg:px-8 bg-white relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-1/2 right-1/4 w-80 h-80 rounded-full blur-[120px] bg-blue-500/5" />
          </div>

          <div className="max-w-7xl mx-auto relative z-10">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              
              {/* Left Column: Text & Features */}
              <motion.div
                initial={{ opacity: 0, x: isRTL ? 50 : -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="space-y-8"
              >
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-200 rounded-full">
                  <Edit className="w-4 h-4 text-blue-600 animate-pulse" />
                  <span className="text-sm font-semibold text-blue-600">
                    {language === 'ar' ? 'مساعد الكتابة بالذكاء الاصطناعي' : 'AI Cover Letter Writer'}
                  </span>
                </div>
                
                <h2 className="text-4xl md:text-5xl font-black text-slate-900 leading-tight">
                  {content.coverLetters.title}
                </h2>
                <p className="text-lg text-slate-600 leading-relaxed">
                  {content.coverLetters.description}
                </p>
                
                <ul className="space-y-4">
                  {content.coverLetters.features.map((feature, index) => (
                    <motion.li 
                      key={index}
                      whileHover={{ x: isRTL ? -4 : 4 }}
                      className="flex items-start gap-3 p-1 rounded-lg hover:bg-slate-50 transition-colors"
                    >
                      <CheckCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700 font-semibold">{feature}</span>
                    </motion.li>
                  ))}
                </ul>

                <Link
                  to="/cv-generation"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-[1.02]"
                >
                  {content.coverLetters.cta}
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </motion.div>

              {/* Right Column: AI Cover Letter Writer Simulator Workspace */}
              <motion.div
                initial={{ opacity: 0, x: isRTL ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="relative bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-2xl overflow-hidden min-h-[440px] flex flex-col justify-between"
              >
                
                {/* Mock Browser Header & Controls */}
                <div className="flex items-center justify-between pb-4 border-b border-slate-800">
                  <div className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-500/80" />
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/80" />
                    <div className="w-2.5 h-2.5 rounded-full bg-green-500/80" />
                    <span className="text-[10px] font-mono text-slate-500 ml-2">{language === 'tr' ? 'cvlogs.ai/onyazi-yazici' : language === 'ar' ? 'cvlogs.ai/كتابة-الخطاب' : 'cvlogs.ai/cover-letter-writer'}</span>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-indigo-500 animate-pulse" />
                    <span className="text-[10px] font-bold text-indigo-400">
                      {language === 'ar' ? 'منشئ الخطابات نشط' : language === 'tr' ? 'Yazıcı Çevrimiçi' : language === 'ar' ? 'الكاتب متصل' : 'Writer Online'}
                    </span>
                  </div>
                </div>

                {/* Main Workspace: Config & Live Editor */}
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4 my-5 items-stretch">
                  
                  {/* Setup Sidebar (4 cols) */}
                  <div className="md:col-span-4 bg-slate-950/60 border border-slate-850 rounded-xl p-3 flex flex-col gap-3">
                    <div>
                      <label className="text-[9px] font-bold text-slate-500 uppercase block mb-1">
                        {language === 'ar' ? 'المسمى الوظيفي' : 'Job Title'}
                      </label>
                      <div className="text-[10px] text-slate-300 font-bold bg-slate-900 border border-slate-800 rounded px-2 py-1 truncate">
                        {language === 'ar' ? 'مطور برمجيات' : language === 'tr' ? 'Yazılım Geliştirici' : language === 'ar' ? 'مطور برمجيات' : 'Software Developer'}
                      </div>
                    </div>
                    
                    <div>
                      <label className="text-[9px] font-bold text-slate-500 uppercase block mb-1">
                        {language === 'ar' ? 'الشركة المستهدفة' : 'Company'}
                      </label>
                      <div className="text-[10px] text-slate-300 font-bold bg-slate-900 border border-slate-800 rounded px-2 py-1 truncate">
                        Bytelogs
                      </div>
                    </div>

                    <div>
                      <label className="text-[9px] font-bold text-slate-500 uppercase block mb-1">
                        {language === 'ar' ? 'أسلوب الكتابة' : 'Writing Tone'}
                      </label>
                      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[8.5px] font-black uppercase bg-blue-500/10 text-blue-400 border border-blue-500/20">
                        <Sparkles className="w-2.5 h-2.5" />
                        {language === 'ar' ? 'احترافي مقنع' : 'Persuasive'}
                      </span>
                    </div>
                  </div>

                  {/* Live typing Editor Screen (8 cols) */}
                  <div className="md:col-span-8 bg-slate-950 border border-slate-850 rounded-xl p-4 min-h-[220px] flex flex-col justify-between relative overflow-hidden">
                    <div className="absolute top-1.5 right-2 flex items-center gap-1">
                      <span className={`w-1.5 h-1.5 rounded-full ${coverLetterStep === 1 ? 'bg-indigo-500 animate-ping' : 'bg-green-500'}`} />
                      <span className="text-[8px] text-slate-500 font-bold uppercase tracking-wider">
                        {coverLetterStep === 1 
                          ? (language === 'ar' ? 'جاري الكتابة...' : 'AI Writing...') 
                          : (language === 'ar' ? 'جاهز للتصدير' : 'Ready to Export')
                        }
                      </span>
                    </div>

                    {/* Typed Content Area */}
                    <div className="text-[11px] text-slate-200 leading-relaxed font-mono whitespace-pre-wrap flex-1 pr-1 overflow-y-auto mt-2">
                      {coverLetterText}
                      {coverLetterStep === 1 && (
                        <span className="inline-block w-1.5 h-3.5 bg-blue-500 ml-0.5 animate-pulse" />
                      )}
                    </div>

                    {/* Bottom action controls */}
                    <div className="border-t border-slate-850/60 pt-3 mt-2 flex justify-between items-center">
                      <span className="text-[9px] text-slate-500 font-bold uppercase">
                        {language === 'ar' ? 'تنسيق الخطاب: رسمي' : 'Format: Professional'}
                      </span>
                      
                      <button
                        disabled={coverLetterStep !== 2}
                        className={`text-[9px] font-black uppercase tracking-wider px-3 py-1.5 rounded-lg border transition-all duration-300 ${
                          coverLetterStep === 2
                            ? 'bg-blue-600 text-white border-blue-500 shadow-md shadow-blue-500/15 hover:scale-105 hover:bg-blue-500'
                            : 'bg-slate-900 border-slate-800 text-slate-600 cursor-not-allowed'
                        }`}
                      >
                        {language === 'ar' ? 'تصدير PDF 📥' : 'Export PDF 📥'}
                      </button>
                    </div>
                  </div>

                </div>

                {/* Progress Indicators */}
                <div className="flex justify-center gap-1.5 pt-3 border-t border-slate-800">
                  {[0, 1, 2].map((stepIdx) => (
                    <span
                      key={stepIdx}
                      className={`h-1.5 rounded-full transition-all duration-300 ${
                        (coverLetterStep === stepIdx) ? 'w-6 bg-blue-500' : 'w-1.5 bg-slate-800'
                      }`}
                    />
                  ))}
                </div>

              </motion.div>

            </div>
          </div>
        </section>

        {/* AI Assistant Section */}
        <section className="py-24 px-4 sm:px-6 lg:px-8 bg-slate-900 text-white relative overflow-hidden">
          {/* Subtle background decorators */}
          <div className="absolute top-1/2 left-1/4 w-80 h-80 rounded-full blur-[120px] bg-blue-500/10 pointer-events-none" />
          <div className="absolute bottom-0 right-10 w-60 h-60 rounded-full blur-[100px] bg-indigo-500/10 pointer-events-none" />
          
          <div className="max-w-7xl mx-auto relative z-10">
            <div className="text-center mb-16">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="text-3xl md:text-4xl font-extrabold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 via-indigo-200 to-emerald-400 animate-pulse bg-[size:200%_auto] tracking-tight">
                  {content.aiAssistant.title}
                </h2>
                <p className="text-base md:text-lg text-slate-400 max-w-2xl mx-auto leading-relaxed">
                  {content.aiAssistant.description}
                </p>
              </motion.div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {content.aiAssistant.features.map((feature, index) => {
                const icons = [
                  <MessageSquare className="w-6 h-6 text-blue-400 group-hover:scale-110 transition-transform duration-300" />,
                  <FileText className="w-6 h-6 text-indigo-400 group-hover:scale-110 transition-transform duration-300" />,
                  <Target className="w-6 h-6 text-emerald-400 group-hover:scale-110 transition-transform duration-300" />,
                  <Briefcase className="w-6 h-6 text-amber-400 group-hover:scale-110 transition-transform duration-300" />
                ];
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    whileHover={{ 
                      y: -8,
                      scale: 1.02,
                      boxShadow: "0 20px 30px -10px rgba(59, 130, 246, 0.2)"
                    }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    className="group bg-slate-800/40 backdrop-blur border border-slate-700/40 rounded-2xl p-6 relative overflow-hidden cursor-pointer hover:border-blue-500/30 transition-all duration-300"
                  >
                    {/* Glowing corner decorator */}
                    <div className="absolute -top-10 -right-10 w-24 h-24 bg-blue-500/10 rounded-full blur-2xl group-hover:bg-blue-500/20 transition-colors duration-500" />
                    
                    <div className="mb-4 inline-flex p-3 rounded-xl bg-slate-900/60 border border-slate-800 group-hover:border-blue-500/30 transition-all duration-300 shadow-inner">
                      {icons[index] || <CheckCircle className="w-6 h-6 text-blue-400" />}
                    </div>
                    
                    <h4 className="text-base font-bold text-slate-100 mb-2 group-hover:text-blue-400 transition-colors duration-300">
                      {feature}
                    </h4>
                    <p className="text-xs text-slate-400 leading-relaxed">
                      {index === 0 && (language === 'ar' ? 'اطرح أي أسئلة مهنية واحصل على ردود فصحى فوراً لتجهيز المقابلات.' : 'Ask any career-related questions and get immediate answers to ace interviews.')}
                      {index === 1 && (language === 'ar' ? 'فحص جودة السيرة ومقارنتها بمتطلبات السوق وحلول مخصصة.' : 'Scan CV quality and match it against market needs with custom feedback.')}
                      {index === 2 && (language === 'ar' ? 'شرح تفصيلي ومحدد لأسباب ترشيح كل وظيفة لملفك الشخصي.' : 'Detailed breakdown of why a job match coordinates with your profile.')}
                      {index === 3 && (language === 'ar' ? 'تابع مراحل التقديم وتحديثات الشركات المهنية أولاً بأول.' : 'Monitor application stages and hiring updates dynamically in real-time.')}
                    </p>
                  </motion.div>
                );
              })}
            </div>

            <div className="text-center mt-16">
              <Link
                to="/login"
                className="inline-flex items-center gap-2 px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-2xl transition-all duration-300 shadow-lg hover:shadow-xl hover:shadow-blue-500/10 hover:scale-105"
              >
                <span>{content.aiAssistant.cta}</span>
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </section>

  {/* Pricing Section */}
        <section id="pricing">
            <PricingSection language={language} />
        </section>


        {/* Learning Section */}
        <section id="learning" className="py-24 px-4 sm:px-6 lg:px-8 bg-slate-50 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-1/2 left-1/3 w-80 h-80 rounded-full blur-[120px] bg-blue-500/5" />
            <div className="absolute bottom-0 right-1/4 w-72 h-72 rounded-full blur-[100px] bg-indigo-500/5" />
          </div>

          <div className="max-w-7xl mx-auto relative z-10">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              
              {/* Left Column: Text & Features */}
              <motion.div
                initial={{ opacity: 0, x: isRTL ? 50 : -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="space-y-8"
              >
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-200 rounded-full">
                  <BookOpen className="w-4 h-4 text-blue-600 animate-pulse" />
                  <span className="text-sm font-semibold text-blue-600">
                    {language === 'ar' ? 'التطوير المهني والتعلم' : 'Continuous Learning'}
                  </span>
                </div>
                
                <h2 className="text-4xl md:text-5xl font-black text-slate-900 leading-tight">
                  {content.learning.title}
                </h2>
                <p className="text-lg text-slate-600 leading-relaxed">
                  {content.learning.description}
                </p>
                
                {/* Visual features layout */}
                <div className="grid sm:grid-cols-2 gap-4">
                  {content.learning.features.map((feature, index) => {
                    const featureIcons = [
                      <Award className="w-5 h-5 text-blue-600" />,
                      <Sparkles className="w-5 h-5 text-indigo-600" />,
                      <FileText className="w-5 h-5 text-emerald-600" />,
                      <User className="w-5 h-5 text-amber-600" />
                    ];
                    return (
                      <motion.div
                        key={index}
                        whileHover={{ scale: 1.02, y: -2 }}
                        className="bg-white border border-slate-200/60 p-4 rounded-xl flex items-center gap-3 shadow-sm"
                      >
                        <div className="p-2 bg-slate-50 rounded-lg border border-slate-100">
                          {featureIcons[index] || <CheckCircle className="w-5 h-5 text-blue-600" />}
                        </div>
                        <span className="text-sm font-bold text-slate-700">{feature}</span>
                      </motion.div>
                    );
                  })}
                </div>

                <Link
                  to="/dashboard"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-[1.02]"
                >
                  {content.learning.cta}
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </motion.div>

              {/* Right Column: Interactive Slideshow Dashboard Simulator */}
              <motion.div
                initial={{ opacity: 0, x: isRTL ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="relative bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-2xl overflow-hidden min-h-[440px] flex flex-col justify-between"
              >
                
                {/* Mock Browser Header & Controls */}
                <div className="flex items-center justify-between pb-4 border-b border-slate-800">
                  <div className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-500/80" />
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/80" />
                    <div className="w-2.5 h-2.5 rounded-full bg-green-500/80" />
                    <span className="text-[10px] font-mono text-slate-500 ml-2">{language === 'tr' ? 'cvlogs.ai/ogrenme-merkezi' : language === 'ar' ? 'cvlogs.ai/مركز-التعلم' : 'cvlogs.ai/learning-hub'}</span>
                  </div>
                  
                  {/* Slider Control Tabs */}
                  <div className="flex bg-slate-950/80 p-0.5 rounded-lg border border-slate-800">
                    {[
                      { id: 0, label: language === 'tr' ? 'Dersler' : language === 'ar' ? 'الدروس' : 'Lessons' },
                      { id: 1, label: language === 'tr' ? 'Yol' : language === 'ar' ? 'المسار' : 'Path' },
                      { id: 2, label: language === 'tr' ? 'Makaleler' : language === 'ar' ? 'المقالات' : 'Articles' }
                    ].map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => setActiveLearningSlide(tab.id)}
                        className={`text-[10px] font-bold px-2.5 py-1 rounded transition-all duration-300 ${
                          activeLearningSlide === tab.id 
                            ? 'bg-blue-600 text-white shadow-md' 
                            : 'text-slate-400 hover:text-slate-200'
                        }`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Animated Moving Screen Workspace Container */}
                <div className="my-6 flex-1 flex flex-col justify-center relative min-h-[260px]">
                  <AnimatePresence mode="wait">
                    
                    {/* Slide 0: Course Video Simulator */}
                    {activeLearningSlide === 0 && (
                      <motion.div
                        key="course-player"
                        initial={{ opacity: 0, scale: 0.95, y: 10 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: -10 }}
                        transition={{ duration: 0.4 }}
                        className="bg-slate-950/70 border border-slate-800 rounded-xl p-5 relative overflow-hidden"
                      >
                        {/* Video Aspect Container */}
                        <div className="bg-slate-900 rounded-lg aspect-video flex flex-col justify-between p-4 border border-slate-800 relative overflow-hidden group">
                          {/* Ambient background glow inside video */}
                          <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-transparent to-purple-500/10 pointer-events-none" />
                          
                          <div className="flex justify-between items-center z-10">
                            <span className="text-[9px] bg-red-600 text-white px-2 py-0.5 rounded font-black tracking-wider uppercase">
                              {language === 'tr' ? 'Premium' : language === 'ar' ? 'مميز' : 'Premium'}
                            </span>
                            <span className="text-[10px] text-slate-400 font-mono">14:28 / 25:00</span>
                          </div>

                          {/* Large Play icon in the center */}
                          <div className="flex justify-center items-center my-2 z-10">
                            <motion.div
                              animate={{ scale: [1, 1.1, 1] }}
                              transition={{ duration: 2, repeat: Infinity }}
                              className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center shadow-lg shadow-blue-500/30 border border-blue-400 cursor-pointer"
                            >
                              <Play className="w-5 h-5 text-white fill-white ml-0.5" />
                            </motion.div>
                          </div>

                          {/* Custom Progress Bar */}
                          <div className="space-y-1.5 z-10">
                            <div className="h-1 bg-slate-800 rounded-full overflow-hidden">
                              <motion.div 
                                className="h-full bg-blue-500" 
                                initial={{ width: "20%" }}
                                animate={{ width: "62%" }}
                                transition={{ duration: 5, repeat: Infinity, repeatType: "reverse" }}
                              />
                            </div>
                            <div className="flex justify-between text-[9px] text-slate-400 font-medium">
                              <span>{language === 'tr' ? 'Ders 3: Davranışsal Mülakat Sorularında Ustalaşmak' : language === 'tr' ? 'Ders 3: Davranışsal Mülakat Sorularında Ustalaşmak' : language === 'ar' ? 'الدرس 3: إتقان أسئلة المقابلات سلوكياً' : 'Lesson 3: Mastering Behavioral Interview Qs'}</span>
                              <span className="text-blue-400">62% Completed</span>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}

                    {/* Slide 1: Career Roadmap Simulator */}
                    {activeLearningSlide === 1 && (
                      <motion.div
                        key="roadmap-path"
                        initial={{ opacity: 0, scale: 0.95, y: 10 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: -10 }}
                        transition={{ duration: 0.4 }}
                        className="bg-slate-950/70 border border-slate-800 rounded-xl p-5"
                      >
                        <h4 className="text-xs font-bold text-slate-400 mb-4 tracking-wider uppercase">
                          {language === 'tr' ? 'Kariyer Gelişim Yol Haritası' : language === 'tr' ? 'Kariyer Gelişim Yol Haritası' : language === 'ar' ? 'خارطة طريق التطوير المهني' : 'Career Growth Roadmap'}
                        </h4>

                        {/* Roadmap Steps */}
                        <div className="flex items-center justify-between relative px-2">
                          {/* Connector Line */}
                          <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-slate-800 -translate-y-1/2 z-0" />
                          
                          {[
                            { id: 1, label: language === 'tr' ? 'CV Oluştur' : language === 'ar' ? 'كتابة السيرة' : 'Build CV', color: 'blue', done: true },
                            { id: 2, label: language === 'tr' ? 'Beceri Geliştirme' : language === 'ar' ? 'تطوير المهارات' : 'Upskilling', color: 'indigo', current: true },
                            { id: 3, label: language === 'tr' ? 'Mülakatlar' : language === 'ar' ? 'المقابلات' : 'Interviews', color: 'purple' },
                            { id: 4, label: language === 'tr' ? 'İşe Alındı 🎉' : language === 'ar' ? 'التوظيف 🎉' : 'Hired 🎉', color: 'emerald' }
                          ].map((step, idx) => (
                            <div key={step.id} className="flex flex-col items-center z-10 relative">
                              <motion.div
                                animate={step.current ? { scale: [1, 1.15, 1], boxShadow: ["0 0 0 0px rgba(99,102,241,0.2)", "0 0 0 8px rgba(99,102,241,0)", "0 0 0 0px rgba(99,102,241,0.2)"] } : {}}
                                transition={{ duration: 2, repeat: Infinity }}
                                className={`w-10 h-10 rounded-xl flex items-center justify-center border font-bold text-xs ${
                                  step.done 
                                    ? 'bg-blue-600 border-blue-500 text-white'
                                    : step.current
                                      ? 'bg-indigo-600 border-indigo-500 text-white'
                                      : 'bg-slate-900 border-slate-800 text-slate-500'
                                }`}
                              >
                                {step.id}
                              </motion.div>
                              <span className={`text-[9px] font-bold mt-2 whitespace-nowrap ${
                                step.current ? 'text-indigo-400' : step.done ? 'text-blue-400' : 'text-slate-500'
                              }`}>
                                {step.label}
                              </span>
                            </div>
                          ))}
                        </div>
                      </motion.div>
                    )}

                    {/* Slide 2: Expert Blog Feed / Articles */}
                    {activeLearningSlide === 2 && (
                      <motion.div
                        key="expert-articles"
                        initial={{ opacity: 0, scale: 0.95, y: 10 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: -10 }}
                        transition={{ duration: 0.4 }}
                        className="space-y-3"
                      >
                        {[
                          {
                            title: language === 'tr' ? 'ATS Filtrelerini Aşmanın 10 Sırrı' : language === 'tr' ? 'ATS Filtrelerini Aşmanın 10 Sırrı' : language === 'ar' ? 'كيف تتجاوز فلترة الـ ATS بنجاح؟' : '10 Secrets to Bypass ATS Filters Successfully',
                            tag: language === 'tr' ? 'Özgeçmiş İpuçları' : language === 'tr' ? 'Özgeçmiş İpuçları' : language === 'ar' ? 'السيرة الذاتية' : 'Resume Tips',
                            readTime: language === 'tr' ? '4 dk okuma' : language === 'tr' ? '4 dk okuma' : language === 'ar' ? 'قراءة 4 دقائق' : '4 min read',
                            color: 'blue'
                          },
                          {
                            title: language === 'tr' ? '2026\'da En Çok Aranan Teknik Beceriler' : language === 'tr' ? '2026\'da En Çok Aranan Teknik Beceriler' : language === 'ar' ? 'أكثر المهارات المطلوبة في عام 2026' : 'Top Technical Skills in High Demand in 2026',
                            tag: language === 'tr' ? 'Kariyer Gelişimi' : language === 'tr' ? 'Kariyer Gelişimi' : language === 'ar' ? 'تطوير المهارات' : 'Career Growth',
                            readTime: language === 'tr' ? '6 dk okuma' : language === 'tr' ? '6 dk okuma' : language === 'ar' ? 'قراءة 6 دقائق' : '6 min read',
                            color: 'indigo'
                          }
                        ].map((post, idx) => (
                          <motion.div
                            key={idx}
                            whileHover={{ x: isRTL ? -4 : 4 }}
                            className="bg-slate-950/70 border border-slate-850 p-3.5 rounded-xl flex items-center justify-between hover:border-slate-700 transition-colors cursor-pointer group"
                          >
                            <div className="flex flex-col gap-1.5 flex-1 min-w-0 pr-2">
                              <div className="flex items-center gap-2">
                                <span className={`text-[9px] font-bold px-2 py-0.5 rounded bg-${post.color}-500/10 text-${post.color}-400 border border-${post.color}-500/20`}>
                                  {post.tag}
                                </span>
                                <span className="text-[9px] text-slate-500 font-medium">{post.readTime}</span>
                              </div>
                              <h5 className="text-xs font-bold text-slate-200 truncate group-hover:text-white">
                                {post.title}
                              </h5>
                            </div>
                            <ChevronRight className={`w-4 h-4 text-slate-500 group-hover:text-white transform transition-transform ${isRTL ? 'rotate-180 group-hover:-translate-x-0.5' : 'group-hover:translate-x-0.5'}`} />
                          </motion.div>
                        ))}
                      </motion.div>
                    )}

                  </AnimatePresence>
                </div>

                {/* Progress Indicators */}
                <div className="flex justify-center gap-1.5 pt-3 border-t border-slate-800">
                  {[0, 1, 2].map((slideIdx) => (
                    <button
                      key={slideIdx}
                      onClick={() => setActiveLearningSlide(slideIdx)}
                      className={`h-1.5 rounded-full transition-all duration-300 ${
                        activeLearningSlide === slideIdx ? 'w-6 bg-blue-500' : 'w-1.5 bg-slate-800'
                      }`}
                      aria-label={`Go to slide ${slideIdx + 1}`}
                    />
                  ))}
                </div>

              </motion.div>

            </div>
          </div>
        </section>

        {/* For Employers Section */}
        <section id="employers" className="py-24 px-4 sm:px-6 lg:px-8 bg-slate-50 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-1/2 left-1/4 w-80 h-80 rounded-full blur-[120px] bg-slate-500/5" />
          </div>

          <div className="max-w-7xl mx-auto relative z-10">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              
              {/* Left Column: Interactive Recruiter Portal Simulator Workspace */}
              <motion.div
                initial={{ opacity: 0, x: isRTL ? 50 : -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="relative bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-2xl overflow-hidden min-h-[440px] flex flex-col justify-between"
              >
                
                {/* Mock Browser Header & Controls */}
                <div className="flex items-center justify-between pb-4 border-b border-slate-800">
                  <div className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-500/80" />
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/80" />
                    <div className="w-2.5 h-2.5 rounded-full bg-green-500/80" />
                    <span className="text-[10px] font-mono text-slate-500 ml-2">{language === 'tr' ? 'cvlogs.ai/ik-ats' : language === 'ar' ? 'cvlogs.ai/بوابة-الشركات' : 'cvlogs.ai/recruiter-ats'}</span>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                    <span className="text-[10px] font-bold text-emerald-400">
                      {language === 'tr' ? 'İşveren Portalı Aktif' : language === 'tr' ? 'İşveren Portalı Aktif' : language === 'ar' ? 'بوابة الشركات نشطة' : 'Employer Portal Live'}
                    </span>
                  </div>
                </div>

                {/* Recruiter Workspace layout */}
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4 my-5 items-stretch">
                  
                  {/* Left Column: ATS Stats & Scanning gauge (5 cols) */}
                  <div className="md:col-span-5 bg-slate-950/60 border border-slate-850 rounded-xl p-3 flex flex-col justify-between">
                    <div>
                      <span className="text-[8px] text-slate-500 font-bold uppercase tracking-wider block mb-2">
                        {language === 'ar' ? 'فحص ومطابقة الـ CVs' : 'CV Matches Scanned'}
                      </span>
                      
                      <div className="flex items-center gap-2 mb-3 bg-slate-900 border border-slate-800 p-2 rounded-lg">
                        <div className="w-2 h-2 rounded-full bg-blue-500 animate-ping" />
                        <span className="text-[10.5px] font-bold text-slate-300">
                          {recruiterStep === 0 
                            ? (language === 'ar' ? 'بانتظار الملفات...' : 'Awaiting uploads...') 
                            : (language === 'tr' ? '142 CV Taranıyor...' : language === 'tr' ? '142 CV Taranıyor...' : language === 'ar' ? 'جاري الفحص لـ 142 سيرة' : 'Scanning 142 CVs...')
                          }
                        </span>
                      </div>
                    </div>

                    {/* Progress Circle Dial */}
                    <div className="relative w-24 h-24 flex items-center justify-center mx-auto my-3">
                      <svg className="w-full h-full transform -rotate-90">
                        <circle cx="48" cy="48" r="38" stroke="#1e293b" strokeWidth="6" fill="transparent" />
                        <motion.circle
                          cx="48"
                          cy="48"
                          r="38"
                          stroke="#10b981"
                          strokeWidth="6"
                          fill="transparent"
                          strokeLinecap="round"
                          strokeDasharray="238.76"
                          animate={{
                            strokeDashoffset: 238.76 * (1 - recruiterMatchScore / 100)
                          }}
                          transition={{ duration: 0.3 }}
                          className="drop-shadow-[0_0_8px_rgba(16,185,129,0.3)]"
                        />
                      </svg>
                      <div className="absolute flex flex-col items-center">
                        <span className="text-xl font-black text-slate-100">{recruiterMatchScore}%</span>
                        <span className="text-[8px] text-slate-500 font-bold uppercase">
                          {language === 'tr' ? 'Aday Eşleşmesi' : language === 'ar' ? 'توافق المرشح' : 'Candidate Match'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Right Column: Top Candidates Shortlist Feed (7 cols) */}
                  <div className="md:col-span-7 bg-slate-950 border border-slate-850 rounded-xl p-4 flex flex-col justify-between min-h-[220px]">
                    <div>
                      <h4 className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-3 pb-1 border-b border-slate-850/60">
                        {language === 'tr' ? 'Önerilen Adaylar' : language === 'ar' ? 'المرشحين الموصى بهم' : 'Recommended Candidates'}
                      </h4>

                      <div className="space-y-2">
                        {/* Candidate 1 */}
                        <div className="bg-slate-900 border border-slate-800/80 p-2.5 rounded-lg flex items-center justify-between">
                          <div className="min-w-0 pr-1">
                            <h5 className="text-[11px] font-bold text-slate-200 truncate">
                              {language === 'ar' ? 'أحمد عبد الرحمن' : 'Ahmed Abdelrahman'}
                            </h5>
                            <p className="text-[9px] text-slate-400 font-medium">React Developer</p>
                          </div>
                          <span className={`text-[8.5px] font-black px-1.5 py-0.5 rounded ${
                            recruiterStep === 2
                              ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                              : 'bg-blue-500/10 text-blue-400 border border-blue-500/20'
                          }`}>
                            {recruiterStep === 2 
                              ? (language === 'tr' ? 'Kısa Listeye Alındı ✅' : language === 'ar' ? 'تم الترشيح ✅' : 'Shortlisted ✅') 
                              : (language === 'tr' ? '%98 Eşleşme' : language === 'ar' ? 'مطابقة 98%' : '98% Match')
                            }
                          </span>
                        </div>

                        {/* Candidate 2 */}
                        <div className="bg-slate-900 border border-slate-800/80 p-2.5 rounded-lg flex items-center justify-between opacity-70">
                          <div className="min-w-0 pr-1">
                            <h5 className="text-[11px] font-bold text-slate-200 truncate">
                              {language === 'tr' ? 'Sarah Mostafa' : language === 'ar' ? 'سارة مصطفى' : 'Sarah Mostafa'}
                            </h5>
                            <p className="text-[9px] text-slate-400 font-medium">UI/UX Designer</p>
                          </div>
                          <span className="text-[8.5px] font-black px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400 border border-blue-500/20">
                            {language === 'tr' ? '%94 Eşleşme' : language === 'ar' ? 'مطابقة 94%' : '94% Match'}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="border-t border-slate-850/60 pt-3 flex justify-between items-center text-[9px] text-slate-500 font-bold uppercase">
                      <span>{language === 'tr' ? 'Aktif Pozisyonlar: 3' : language === 'ar' ? 'شواغر نشطة: 3' : 'Active Openings: 3'}</span>
                      <span className="text-emerald-400">
                        {recruiterStep === 2 ? (language === 'tr' ? 'Filtreleme tamamlandı' : language === 'ar' ? 'اكتملت تصفية المواهب' : 'Filter completed') : ''}
                      </span>
                    </div>
                  </div>

                </div>

                {/* Progress Indicators */}
                <div className="flex justify-center gap-1.5 pt-3 border-t border-slate-800">
                  {[0, 1, 2].map((stepIdx) => (
                    <span
                      key={stepIdx}
                      className={`h-1.5 rounded-full transition-all duration-300 ${
                        (recruiterStep === stepIdx) ? 'w-6 bg-blue-500' : 'w-1.5 bg-slate-800'
                      }`}
                    />
                  ))}
                </div>
              </motion.div>

              {/* Right Column: Text & Features */}
              <motion.div
                initial={{ opacity: 0, x: isRTL ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="space-y-8"
              >
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 border border-slate-350 rounded-full">
                  <Building2 className="w-4 h-4 text-slate-700 animate-pulse" />
                  <span className="text-sm font-semibold text-slate-700">For Companies</span>
                </div>
                
                <h2 className="text-4xl md:text-5xl font-black text-slate-900 leading-tight">
                  {content.employers.title}
                </h2>
                <p className="text-lg text-slate-600 leading-relaxed">
                  {content.employers.description}
                </p>
                
                <ul className="space-y-4">
                  {content.employers.features.map((feature, index) => (
                    <motion.li 
                      key={index}
                      whileHover={{ x: isRTL ? -4 : 4 }}
                      className="flex items-start gap-3 p-1 rounded-lg hover:bg-slate-100/50 transition-colors"
                    >
                      <CheckCircle className="w-6 h-6 text-slate-700 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700 font-semibold">{feature}</span>
                    </motion.li>
                  ))}
                </ul>

                <div className="flex flex-col sm:flex-row gap-4">
                  <Link
                    to="/dashboard"
                    className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-slate-800 hover:bg-slate-900 text-white font-bold rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-[1.02]"
                  >
                    {content.employers.ctaPrimary}
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                  <button className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-slate-800 text-slate-800 font-bold rounded-xl transition-all duration-300 bg-white hover:bg-slate-50 shadow-sm hover:shadow transform hover:scale-[1.02]">
                    {content.employers.ctaSecondary}
                  </button>
                </div>
              </motion.div>

            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-24 px-4 sm:px-6 lg:px-8 bg-slate-50">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <Rocket className="w-16 h-16 mx-auto mb-6 text-blue-600" />
              <h2 className="text-4xl md:text-5xl font-black text-slate-900 mb-6">
                {currentUser ? content.cta.titleUser : content.cta.title}
              </h2>
              <p className="text-xl text-slate-600 mb-8">
                {currentUser ? content.cta.descriptionUser : content.cta.description}
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                <Link
                  to={currentUser ? "/cv-builder" : "/register"}
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  {currentUser ? content.cta.ctaPrimaryUser : content.cta.ctaPrimary}
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link
                  to="/resumeChat"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-blue-600 text-blue-600 font-semibold rounded-xl transition-all duration-300 bg-white hover:bg-blue-50"
                >
                  {content.cta.ctaSecondary}
                </Link>
              </div>

              <div className="flex flex-wrap justify-center gap-6">
                {content.cta.badges.map((badge, index) => (
                  <div key={index} className="flex items-center gap-2 text-slate-600">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="font-medium">{badge}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Team Section */}
        <TeamSection />

        {/* Contact Us Section */}
        <section id="contact" className="py-16 px-4 sm:px-6 lg:px-8 bg-white border-t border-slate-100">
          <div className="max-w-3xl mx-auto text-center" dir={isRTL ? 'rtl' : 'ltr'}>
            <h2 className="text-3xl md:text-4xl font-black text-slate-900 mb-3">
              {language === 'ar' ? 'تواصل معنا' : 'Contact Us'}
            </h2>
            <p className="text-slate-600 mb-8">
              {language === 'ar' ? 'نحن هنا لمساعدتك. تواصل معنا عبر البريد الإلكتروني أو الهاتف.' : 'We\'re here to help. Reach out to us via email or phone.'}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <a
                href="mailto:contact@cvlogs.com"
                className="inline-flex items-center gap-3 px-6 py-3 bg-slate-50 hover:bg-blue-50 border border-slate-200 hover:border-blue-300 rounded-xl transition-all duration-200"
              >
                <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                <span className="text-slate-800 font-medium">contact@cvlogs.com</span>
              </a>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-slate-950 text-white pt-16 pb-10 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
          <div className="absolute inset-0 opacity-40" style={{ background: 'radial-gradient(circle at top, rgba(37,99,235,0.4), transparent 60%)' }}></div>
          <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'linear-gradient(120deg, rgba(255,255,255,0.03) 25%, transparent 25%, transparent 50%, rgba(255,255,255,0.03) 50%, rgba(255,255,255,0.03) 75%, transparent 75%, transparent)', backgroundSize: '30px 30px' }}></div>
          
          <div className="relative max-w-6xl mx-auto">
            <div className={`flex flex-col gap-8 lg:flex-row lg:items-center lg:justify-between ${isRTL ? 'lg:flex-row-reverse' : ''}`}>
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-white/10 border border-white/10 flex items-center justify-center shadow-lg shadow-blue-500/30">
                  <img src={footerLogo} alt="CVLogs" className="w-10 h-10 object-contain" loading="lazy" />
                </div>
                <div className={`${isRTL ? 'text-right' : 'text-left'}`}>
                  <p className="text-xs uppercase tracking-[0.4em] text-blue-300">CVLogs</p>
                  <h3 className="text-2xl font-black text-white">BYTELOGS LTD</h3>
                  <p className="text-sm text-slate-300">{language === 'ar' ? 'حلول مدعومة بالذكاء الاصطناعي للمسارات المهنية' : 'AI-powered career intelligence platform'}</p>
                </div>
              </div>

              <div className={`${isRTL ? 'text-right' : 'text-left'} space-y-2`}>
                <p className="text-sm text-slate-400">
                  © 2025 BYTELOGS LTD — {language === 'tr' ? 'Tüm hakları saklıdır' : language === 'ar' ? 'جميع الحقوق محفوظة' : 'All rights reserved'}
                </p>
                <p className="text-sm text-slate-400">
                  BYTELOGS LTD is the legal owner and service provider of this platform.
                </p>
              </div>
            </div>

            <div className="mt-10 flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
              <div className="flex flex-wrap gap-3 text-sm text-slate-300">
                {footerLinks.map((link) => (
                  <button
                    key={link.label}
                    type="button"
                    onClick={() => openLegalModal(link.slug)}
                    className="rounded-full border border-white/10 px-4 py-1.5 hover:border-white/40 hover:text-white transition-colors"
                  >
                    {link.label}
                  </button>
                ))}
              </div>

              <div className="text-xs uppercase tracking-[0.3em] text-slate-500">
                {language === 'ar' ? 'مصمم في الشرقيه' : 'Crafted in Sharqia'}
              </div>
            </div>
          </div>
        </footer>
      </main>

      <LegalDocumentsModal
        isOpen={isLegalModalOpen}
        initialSlug={requestedLegalSlug}
        onClose={() => setIsLegalModalOpen(false)}
        language={language}
        isRTL={isRTL}
      />
    </>
  );
}
